import React from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Search, Plus, CalendarDays, List, X } from 'lucide-react';
import { api } from '@/lib/api';
import { EMPLOYEE_REF_QUERY } from '@/lib/ref-defaults';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { AddressAutocomplete } from '@/components/shared/address-autocomplete';
import { ADDRESS_FIELDS } from '@/components/shared/crud-page';
import { ScheduleBuilder } from '@/components/shared/schedule-builder';
import { extractAddressParts } from '@/lib/mapbox';
import { usePermissions } from '@/hooks/use-permissions';
import { useConfirm } from '@/hooks/use-confirm';
import {
  fmtDate, fmtDateTime, toDateInputValue, parseNzWallToUtc,
  startOfNzDay, addNzDays, nzDayOfWeek,
} from '@/lib/format-date';
import { validateRecurringDuration, validateTimeRange, DISPATCH_JOB_STATUSES } from '@atlas/shared';
import { NzDateTimeInput } from '@/components/shared/nz-datetime-input';
import { rowGestureRange, showsGhost } from './row-gesture';
import { LookupField, refToId } from '@/components/shared/lookup-field';
import {
  CustomerPicker, customerPickerError, customerPickerPayload, type CustomerPickerValue,
} from '@/components/shared/customer-picker';
import { SCHEDULED, scheduleBarClass, statusChipClass } from '@/pages/my-dispatch/status';

// TODO(admin-settings): move these four constants onto an AppSetting-backed config
// so the user can configure them from the Settings page.
const CONFIG = {
  gridStartHour: 0,
  gridEndHour: 23,
  barTitleField: 'info' as const,
  /** Placeholder template for the asset-row label. Replace {field} with asset.field. */
  assetRowTemplate: '{fleetNumber} - {registrationNumber}',
};

// Recurring is being retired from the UI in favour of manual "Duplicate"
// (right-click a job bar, or the edit dialog). The server-side recurrence
// engine + model fields are left intact, so flipping this back to `true`
// restores the cron toggle in the create/edit dialogs unchanged.
const RECURRING_UI_ENABLED = false;

// Desktop dimensions — the live values are picked at render time so the
// asset column shrinks (and hours compress) on mobile/tablet portrait.
const HOUR_WIDTH_DESKTOP = 50;
const HOUR_WIDTH_MOBILE  = 32;
const ASSET_COL_WIDTH_DESKTOP = 240;
const ASSET_COL_WIDTH_MOBILE  = 110;
const ROW_HEIGHT = 40;

/** Full-screen-on-mobile/tablet, modal-on-desktop dialog shell. The long dispatch
 *  forms genuinely want the whole screen below `lg` (1024px) — a cutoff that
 *  covers phones AND iPad-class tablets in portrait — then the modal layout at
 *  lg+.
 *
 *  This is an OPT-IN: DialogContent's own mobile default is a centred card sized
 *  to its content (short dialogs shouldn't be padded out to a screen of empty
 *  space), so the sizing here has to be restated at the base rule's own
 *  `max-lg:!` specificity for twMerge to keep it. Plain unprefixed classes would
 *  lose to the base's important ones below `lg`. */
const FULLSCREEN_DIALOG_CLASS =
  'p-0 gap-0 max-w-none w-screen h-[100dvh] max-h-[100dvh] ' +
  'inset-0 left-0 top-0 translate-x-0 translate-y-0 rounded-none ' +
  'flex flex-col ' +
  'max-lg:!w-screen max-lg:!h-[100dvh] max-lg:!max-h-[100dvh] max-lg:!rounded-none ' +
  // Edge-to-edge starts at 0, not at the base rule's 1rem-plus-inset top anchor.
  'max-lg:!top-0 max-lg:!translate-y-0 ' +
  // Edge-to-edge, so this one DOES have to reserve the iOS insets itself —
  // nothing above it does. Pointing `--dialog-safe-*` at the real values moves
  // both the padding below and the close button in dialog.tsx.
  'max-lg:[--dialog-safe-top:var(--safe-top)] max-lg:[--dialog-safe-bottom:var(--safe-bottom)] ' +
  // This shell supplies its own header/footer chrome, so it wants the insets
  // *only* — not the 1.5rem of body padding a card would pair with them.
  'max-lg:!pt-[var(--dialog-safe-top)] max-lg:!pb-[var(--dialog-safe-bottom)] ' +
  'lg:max-w-5xl lg:w-[calc(100vw-4rem)] lg:h-auto lg:max-h-[90vh] ' +
  'lg:inset-auto lg:left-[50%] lg:top-[50%] lg:translate-x-[-50%] lg:translate-y-[-50%] ' +
  'lg:rounded-lg lg:p-6 lg:gap-4';

// CENTERED_DIALOG_CLASS used to live here: a pile of `max-lg:!` overrides that
// dragged a modal back from full-screen to a centred card. That IS the
// DialogContent default now, so the small modals (Generate Run Sheet, the mobile
// New-Job picker) just take it and the override is gone.

/** Subscribe to the same `md` breakpoint Tailwind uses (max-width: 767px). */
function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = () => setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);
  return isMobile;
}

interface AssetRow {
  id: string;
  fleetNumber: string;
  registrationNumber?: string;
  assetType?: string;
  // Post-migration the field is a populated `{ id, name }` object; older
  // cached responses may still carry the legacy string. Accept both.
  dispatchGroup?: string | { id?: string; _id?: string; name?: string } | null;
  vehicleType?: { type: string };
  vehicleGroup?: { group: string };
  company?: { name: string };
}

const ALL_TAB = '__all__';

interface JobRow {
  id: string;
  _id?: string;
  asset?: { _id?: string } | string | null;
  start?: string | null;
  end?: string | null;
  date?: string | null;
  status?: string | null;
  info?: string | null;
  jobDetails?: string | null;
  customer?: string | null;
  jobNumber?: string | null;
  fleetNumber?: string | null;
}

// Every day this page labels or buckets by is an NZ calendar day — the depot's
// day, not the viewer's. The JS runtime's local timezone (setHours/getDay/
// toLocaleDateString with no timeZone) shifts both for anyone outside NZ, and
// `toISOString().slice(0,10)` shifted the "Jump to date" picker for NZ users
// too: NZ midnight is 12:00 UTC the *previous* day, so the picker read a day
// behind the header until noon. Anchor everything on `nzYmd` instead.
const NZ_TZ = 'Pacific/Auckland';

/** NZ calendar parts of an instant, as [year, month(1-12), day]. */
function nzYmd(d: Date): [number, number, number] {
  const [y, m, dd] = toDateInputValue(d).split('-').map(Number);
  return [y, m, dd];
}
/** en-GB formatting pinned to NZ wall-clock. */
function fmtNz(d: Date, opts: Intl.DateTimeFormatOptions): string {
  return d.toLocaleDateString('en-GB', { ...opts, timeZone: NZ_TZ });
}
const startOfDay = startOfNzDay;
const addDays = addNzDays;
function startOfWeek(d: Date): Date {
  const dow = nzDayOfWeek(d); // 0=Sun
  return addDays(startOfDay(d), dow === 0 ? -6 : 1 - dow); // Monday start
}
function fmtDateRange(start: Date, end: Date): string {
  const sameMonth = nzYmd(start)[1] === nzYmd(end)[1];
  const s = fmtNz(start, { month: 'short', day: 'numeric' });
  const e = sameMonth
    ? fmtNz(end, { day: 'numeric' })
    : fmtNz(end, { month: 'short', day: 'numeric' });
  return `${s} – ${e}, ${nzYmd(end)[0]}`;
}
function fmtShort(d: Date): string {
  return fmtNz(d, { weekday: 'short', day: 'numeric', month: 'numeric' });
}
function resolveRef(v: unknown): string | null {
  if (!v) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && v) return (v as any)._id || (v as any).id || null;
  return null;
}
function fillTemplate(tpl: string, asset: AssetRow): string {
  return tpl.replace(/\{(\w+)\}/g, (_, k) => {
    // Contractor vehicles often share a rego/fleet number with the Atlas asset
    // that covers the same plant, so the dispatch board labels them "Contractor"
    // instead of the rego to disambiguate the two rows.
    if (k === 'registrationNumber' && asset.assetType === 'Contractor') return 'Contractor';
    return (asset as any)[k] ?? '';
  });
}

// ---------- Mobile / tablet-portrait dispatch view ---------------------------
// Two layouts toggle inside this component: a Day calendar (Apple Calendar
// style) and a Job list (chronological). Both reuse the page's already-fetched
// assets + jobs and the existing dialogs for create / edit.

interface MobileScheduleProps {
  assets: AssetRow[];
  jobs: JobRow[];
  anchor: Date;
  setAnchor: (d: Date) => void;
  onJobTap: (jobId: string) => void;
  onAddJob?: (assetId: string, hourOfDay: number) => void;
}

function MobileSchedule({ assets, jobs, anchor, setAnchor, onJobTap, onAddJob }: MobileScheduleProps) {
  const [mode, setMode] = React.useState<'day' | 'list'>('day');
  // Quick-pick dialog state for the FAB. Drives the asset+time selection
  // before delegating to the parent's onAddJob (which opens the full create
  // dialog with the picked draft pre-filled).
  // Hour the FAB creates at: the next whole hour when viewing today, else 9am.
  // Clamped to the grid. The create dialog can edit it, so this is only a seed.
  const fabHour = React.useMemo(() => {
    const now = new Date();
    const isToday = now.toDateString() === startOfDay(anchor).toDateString();
    const h = isToday ? Math.min(CONFIG.gridEndHour, now.getHours() + 1) : 9;
    return Math.max(CONFIG.gridStartHour, Math.min(CONFIG.gridEndHour, h));
  }, [anchor]);
  // Force "day" anchor (week mode doesn't fit on a phone). Strip time part.
  const day = React.useMemo(() => startOfDay(anchor), [anchor]);
  const dayEnd = React.useMemo(() => addDays(day, 1), [day]);

  // Include any job whose [start, end] interval overlaps the viewed day —
  // a multi-day job (e.g. an asset booked for two weeks) should appear on
  // EVERY day in its span, not just the start day. `_dayRel` tells the row
  // renderer what kind of slice this is so it can show the right label
  // ('starts 3:00 AM' / 'all day' / 'ends 10:00 AM' / time-range).
  const todaysJobs = React.useMemo(() => {
    const startTs = day.getTime();
    const endTs = dayEnd.getTime();
    const out: Array<JobRow & { _start: Date; _end: Date; _dayRel: 'same-day' | 'starts' | 'ends' | 'spans' }> = [];
    for (const j of jobs) {
      const start = j.start ? new Date(j.start) : (j.date ? new Date(j.date) : null);
      const end   = j.end   ? new Date(j.end)   : (start ? new Date(start.getTime() + 60 * 60 * 1000) : null);
      if (!start) continue;
      const eff = end ?? start;
      if (start.getTime() >= endTs || eff.getTime() <= startTs) continue;
      const startsToday = start.getTime() >= startTs && start.getTime() < endTs;
      const endsToday   = eff.getTime() > startTs && eff.getTime() <= endTs;
      const dayRel: 'same-day' | 'starts' | 'ends' | 'spans' =
        startsToday && endsToday ? 'same-day'
        : startsToday            ? 'starts'
        : endsToday              ? 'ends'
        : 'spans';
      out.push({ ...j, _start: start, _end: eff, _dayRel: dayRel });
    }
    // Sort: spans first (top of grid as "all-day"), then by the time-of-day
    // that's actually relevant on THIS day (start hour for starts/same-day,
    // end hour for ends).
    return out.sort((a, b) => {
      const rank = (j: typeof out[number]): number => {
        if (j._dayRel === 'spans')  return 0;
        if (j._dayRel === 'ends')   return j._end.getTime();
        return j._start.getTime();
      };
      return rank(a) - rank(b);
    });
  }, [jobs, day, dayEnd]);

  const assetById = React.useMemo(() => {
    const m = new Map<string, AssetRow>();
    for (const a of assets) m.set(a.id, a);
    return m;
  }, [assets]);

  function jobAssetLabel(j: JobRow): string {
    const aid = typeof j.asset === 'string' ? j.asset : (j.asset?._id ?? null);
    if (!aid) return j.fleetNumber ?? '—';
    const a = assetById.get(aid);
    return a ? (a.fleetNumber ?? '—') : (j.fleetNumber ?? '—');
  }

  return (
    <div className="lg:hidden space-y-2">
      {/* Day navigator — Day/List toggle and "+" New are floating, see below. */}
      <div className="flex items-center gap-1">
        <Button aria-label="Previous day" size="sm" variant="outline" onClick={() => setAnchor(addDays(day, -1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={() => setAnchor(startOfDay(new Date()))}>Today</Button>
        <Button aria-label="Next day" size="sm" variant="outline" onClick={() => setAnchor(addDays(day, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <div className="flex-1 text-sm font-medium px-2 truncate">
          {fmtNz(day, { weekday: 'short', month: 'short', day: 'numeric' })}
        </div>
      </div>

      {mode === 'day' ? (
        <MobileDayView
          assets={assets}
          todaysJobs={todaysJobs}
          jobAssetLabel={jobAssetLabel}
          onJobTap={onJobTap}
          onAddJob={onAddJob}
        />
      ) : (
        <MobileListView
          todaysJobs={todaysJobs}
          jobAssetLabel={jobAssetLabel}
          onJobTap={onJobTap}
        />
      )}

      {/* Floating controls — Day/List view toggle sits to the LEFT of the
          "+" New FAB. Icon-only so the cluster stays compact. Anchored at
          `bottom-20` so it clears the mobile bottom-nav (h-16 = 64px) by
          ~16px of breathing room. */}
      <div className="fixed bottom-20 right-5 z-30 flex items-center gap-2 lg:hidden">
        <div className="inline-flex rounded-full border bg-background shadow-lg p-1">
          <button
            type="button"
            onClick={() => setMode('day')}
            aria-label="Day view"
            aria-pressed={mode === 'day'}
            className={cn(
              'inline-flex items-center justify-center h-10 w-10 rounded-full transition-colors',
              mode === 'day' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <CalendarDays className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => setMode('list')}
            aria-label="List view"
            aria-pressed={mode === 'list'}
            className={cn(
              'inline-flex items-center justify-center h-10 w-10 rounded-full transition-colors',
              mode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <List className="h-5 w-5" />
          </button>
        </div>
        {onAddJob && (
          <Button
            type="button"
            onClick={() => onAddJob('', fabHour)}
            aria-label="New dispatch job"
            className="h-14 w-14 rounded-full shadow-lg p-0"
          >
            <Plus className="h-6 w-6" />
          </Button>
        )}
      </div>
    </div>
  );
}

function MobileDayView({
  assets, todaysJobs, jobAssetLabel, onJobTap, onAddJob,
}: {
  assets: AssetRow[];
  todaysJobs: Array<JobRow & { _start: Date; _end: Date; _dayRel: 'same-day' | 'starts' | 'ends' | 'spans' }>;
  jobAssetLabel: (j: JobRow) => string;
  onJobTap: (id: string) => void;
  /** assetId is '' when more than one asset is in scope — the create dialog
   *  then collects the vehicle itself. */
  onAddJob?: (assetId: string, hour: number) => void;
}) {
  // Vertical hour rows from gridStartHour..gridEndHour. Each row shows the
  // jobs that start in that hour. Tap an empty hour to add (when an asset
  // is pre-selectable) or tap a job to edit.
  const startHour = CONFIG.gridStartHour;
  const endHour   = CONFIG.gridEndHour;
  const hours: number[] = [];
  for (let h = startHour; h <= endHour; h++) hours.push(h);

  const jobsByHour = React.useMemo(() => {
    const m = new Map<number, typeof todaysJobs>();
    for (const j of todaysJobs) {
      // Bucket by the NZ-local hour that's relevant to THIS day. Multi-day
      // tails get pinned to the start-of-day row so they don't disappear.
      const h = j._dayRel === 'spans' ? startHour
              : j._dayRel === 'ends'  ? nzHourOf(j._end)
              :                         nzHourOf(j._start);
      const arr = m.get(h) ?? [];
      arr.push(j);
      m.set(h, arr);
    }
    return m;
  }, [todaysJobs, startHour]);

  // Tapping an empty hour always opens the create dialog, matching the web
  // Gantt. With one asset in scope the vehicle is implied and comes prefilled;
  // with several (e.g. the "All" filter) it is passed empty and the dialog
  // shows a vehicle picker instead of a read-only field. Previously the
  // multi-asset case rendered an inert spacer, making every empty hour row a
  // dead zone on a phone.
  const onlyAsset = assets.length === 1 ? assets[0] : null;

  return (
    <Card>
      <CardContent className="p-0 divide-y">
        {hours.map((h) => {
          const slot = jobsByHour.get(h) ?? [];
          return (
            <div key={h} className="grid grid-cols-[56px_1fr] gap-2 px-3 py-2">
              <div className="text-xs text-muted-foreground pt-1 tabular-nums">
                {String(h).padStart(2, '0')}:00
              </div>
              <div className="space-y-1.5">
                {slot.length === 0 && onAddJob && (
                  // Deliberately unlabelled: empty time is tappable but shows
                  // no affordance, matching the web Gantt where bare grid space
                  // opens the create dialog. aria-label carries the meaning for
                  // screen readers since there is no visible text.
                  <button
                    type="button"
                    onClick={() => onAddJob(onlyAsset?.id ?? '', h)}
                    aria-label={`Add dispatch job at ${String(h).padStart(2, '0')}:00`}
                    className="w-full h-9 rounded-md hover:bg-accent/40"
                  />
                )}
                {slot.length === 0 && !onAddJob && (
                  <div className="h-7" />
                )}
                {slot.map((j) => (
                  <button
                    key={j.id}
                    type="button"
                    onClick={() => onJobTap(j.id)}
                    className="w-full text-left rounded-md border bg-primary/10 hover:bg-primary/20 px-2.5 py-2 transition-colors"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-sm font-medium truncate">{jobAssetLabel(j)}</div>
                      <div className="text-xs text-muted-foreground tabular-nums shrink-0">
                        {renderTimeRange(j)}
                      </div>
                    </div>
                    {(j.customer || j.info) && (
                      <div className="text-xs text-muted-foreground truncate mt-0.5">
                        {j.customer || j.info}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function MobileListView({
  todaysJobs, jobAssetLabel, onJobTap,
}: {
  todaysJobs: Array<JobRow & { _start: Date; _end: Date; _dayRel: 'same-day' | 'starts' | 'ends' | 'spans' }>;
  jobAssetLabel: (j: JobRow) => string;
  onJobTap: (id: string) => void;
}) {
  // Bucket by NZ morning / afternoon / evening using the time-of-day relevant
  // to THIS day: start hour for jobs that start today, end hour for jobs that
  // end today, "all day" for jobs that started earlier and end later.
  const bucketHour = (j: typeof todaysJobs[number]): number =>
    j._dayRel === 'spans' ? 0
    : j._dayRel === 'ends' ? nzHourOf(j._end)
    : nzHourOf(j._start);
  const buckets: Array<{ label: string; jobs: typeof todaysJobs }> = [
    { label: 'Morning (before noon)', jobs: todaysJobs.filter((j) => bucketHour(j) < 12) },
    { label: 'Afternoon (12 – 5 PM)', jobs: todaysJobs.filter((j) => bucketHour(j) >= 12 && bucketHour(j) < 17) },
    { label: 'Evening (5 PM onward)', jobs: todaysJobs.filter((j) => bucketHour(j) >= 17) },
  ];

  if (todaysJobs.length === 0) {
    return <div className="text-sm text-muted-foreground text-center py-8">No jobs scheduled today.</div>;
  }

  return (
    <div className="space-y-3">
      {buckets.filter((b) => b.jobs.length > 0).map((b) => (
        <div key={b.label}>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1 pb-1">{b.label}</div>
          <Card>
            <CardContent className="p-0 divide-y">
              {b.jobs.map((j) => (
                <button
                  key={j.id}
                  type="button"
                  onClick={() => onJobTap(j.id)}
                  className="w-full text-left px-3 py-2.5 hover:bg-accent/30"
                >
                  <div className="flex items-baseline gap-3">
                    <div className="text-xs text-muted-foreground tabular-nums w-20 shrink-0">
                      {renderTimeRange(j)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{jobAssetLabel(j)}</div>
                      {(j.customer || j.info) && (
                        <div className="text-xs text-muted-foreground truncate">{j.customer || j.info}</div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>
      ))}
    </div>
  );
}

function fmtTime(d: Date): string {
  // 24-hour format to match the dashboard's 24-hour timeline. Pacific/Auckland
  // is forced so the time matches the depot's wall-clock regardless of the
  // viewer's browser tz.
  return d.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Pacific/Auckland' });
}

/** Mini-card / list time label that's truthful for multi-day jobs. */
function renderTimeRange(j: { _start: Date; _end: Date; _dayRel: 'same-day' | 'starts' | 'ends' | 'spans' }): string {
  switch (j._dayRel) {
    case 'same-day': return `${fmtTime(j._start)}–${fmtTime(j._end)}`;
    case 'starts':   return `starts ${fmtTime(j._start)}`;
    case 'ends':     return `ends ${fmtTime(j._end)}`;
    case 'spans':    return 'all day';
  }
}

/** NZ hour-of-day for a Date — used by the hour-grid bucketing. */
const NZ_HOUR_FMT = new Intl.DateTimeFormat('en-CA', { timeZone: 'Pacific/Auckland', hour: '2-digit', hour12: false });
function nzHourOf(d: Date): number {
  const h = Number(NZ_HOUR_FMT.formatToParts(d).find((p) => p.type === 'hour')?.value ?? '0');
  return h === 24 ? 0 : h;
}

export function DispatchSchedulePage() {
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canAddDispatchJob = can('dispatch_jobs', 'add');
  // Compress the timeline on small screens so the sticky vehicle column
  // doesn't eat 60% of a phone viewport.
  const isMobile = useIsMobileViewport();
  const HOUR_WIDTH = isMobile ? HOUR_WIDTH_MOBILE : HOUR_WIDTH_DESKTOP;
  const ASSET_COL_WIDTH = isMobile ? ASSET_COL_WIDTH_MOBILE : ASSET_COL_WIDTH_DESKTOP;
  const [view, setView] = React.useState<'week' | 'day'>('day');
  const [anchor, setAnchor] = React.useState<Date>(() => startOfDay(new Date()));
  const [search, setSearch] = React.useState('');
  const [activeGroup, setActiveGroup] = React.useState<string>(ALL_TAB);
  const [genOpen, setGenOpen] = React.useState(false);
  const [editingJobId, setEditingJobId] = React.useState<string | null>(null);
  const [drag, setDrag] = React.useState<
    {
      assetId: string;
      startX: number;
      currentX: number;
      rowLeft: number;
      /** Which input started the gesture — decides range-drag vs tap-to-create. */
      pointerType: string;
      /** pointerdown timeStamp, for the touch tap-vs-long-press cutoff. */
      startedAt: number;
    } | null
  >(null);
  const [newJobDraft, setNewJobDraft] = React.useState<
    { assetId: string; start: Date; end: Date } | null
  >(null);
  // Duplicate flow — the source job whose data is being cloned into a new
  // record (opened from the bar right-click menu or the edit dialog).
  const [duplicatingJobId, setDuplicatingJobId] = React.useState<string | null>(null);
  // Right-click context menu on a desktop job bar. Anchored at the pointer;
  // currently offers Duplicate. Closed on outside-click / Esc / scroll.
  const [jobMenu, setJobMenu] = React.useState<{ jobId: string; x: number; y: number } | null>(null);

  React.useEffect(() => {
    if (!jobMenu) return;
    const close = () => setJobMenu(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setJobMenu(null); };
    // The bar's onContextMenu stops propagation, so a right-click elsewhere
    // (empty grid, another bar already replaced state) closes this menu.
    window.addEventListener('click', close);
    window.addEventListener('contextmenu', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('contextmenu', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [jobMenu]);

  // Bar manipulation: move (with optional cross-row reassignment) and
  // edge-handle resize. Activated by pointer-down on an existing job bar
  // — the row-background drag-to-create handler is suppressed for these
  // since the bar itself stops propagation. A small 3px deadzone lets a
  // genuine click still open the edit dialog (we only commit a move/
  // resize once `moved` flips to true).
  type Interaction =
    | {
        kind: 'move';
        jobId: string;
        origAssetId: string;
        origStartMs: number;
        durationMs: number;
        cursorOffsetX: number;        // px between pointerX-at-start and bar.left
        startClientX: number;         // pointer-down client coords (for movement test)
        startClientY: number;
        clientX: number;              // latest pointer client coords
        clientY: number;
        targetAssetId: string;        // current asset under the pointer
        targetRowLeft: number;        // its row's left edge in client coords
        moved: boolean;
      }
    | {
        kind: 'resize-start' | 'resize-end';
        jobId: string;
        assetId: string;
        origStartMs: number;
        origEndMs: number;
        rowLeft: number;              // origin row's left edge in client coords
        startClientX: number;
        currentX: number;             // latest pointerX relative to rowLeft
        moved: boolean;
      };
  const [interaction, setInteraction] = React.useState<Interaction | null>(null);
  // Pending change captured on drop. The user confirms (PUT) or cancels
  // (discard). We deliberately don't apply the change optimistically —
  // the bar stays where it was so the dialog isn't lying about state.
  type PendingChange =
    | { kind: 'move'; jobId: string; jobLabel: string;
        fromStart: Date; fromEnd: Date; toStart: Date; toEnd: Date;
        fromAssetLabel: string; toAssetLabel: string;
        nextAssetId: string; assetChanged: boolean; }
    | { kind: 'resize'; jobId: string; jobLabel: string; edge: 'start' | 'end';
        fromStart: Date; fromEnd: Date; toStart: Date; toEnd: Date; };
  const [pendingChange, setPendingChange] = React.useState<PendingChange | null>(null);

  // Visible range.
  const days = view === 'week' ? 7 : 1;
  const rangeStart = view === 'week' ? startOfWeek(anchor) : startOfDay(anchor);
  const rangeEnd = addDays(rangeStart, days); // exclusive

  // Assets where showInDispatch === true. Atlas-owned and Contractor live
  // in separate routes (/fleet/assets is scoped to Atlas; /fleet/contractor-
  // vehicles to Contractor), but the dispatch grid wants them merged so the
  // dispatcher sees one timeline.
  const { data: assetResp } = useQuery({
    queryKey: ['dispatch-grid-assets'],
    queryFn: async () => {
      // Dispatch-scoped endpoint — gated on dispatch_page view permission
      // instead of assets view, so a dispatcher without Assets-report
      // access can still see the grid's rows. Server returns Atlas +
      // Contractor assets merged in one call (was two before).
      const r = await api<AssetRow[]>('/fleet/dispatch-assets?limit=999');
      return r.data ?? [];
    },
  });
  const allAssets = React.useMemo(() => assetResp ?? [], [assetResp]);

  // Dispatch groups list — drives the tab strip. Fetched directly from
  // master-data so a freshly created group appears as a tab even before any
  // asset is assigned to it, and a renamed group's tab updates immediately.
  // Server returns alphabetical-by-name (matches the EMPLOYEE_REF_QUERY
  // pattern); we keep that order for the tabs.
  const { data: dispatchGroupRows } = useQuery({
    queryKey: ['dispatch-grid-groups'],
    queryFn: async () => (await api<Array<{ id: string; name: string }>>('/master-data/dispatch-groups?limit=500&sort=name&order=asc')).data ?? [],
  });

  // Coerce dispatchGroup (populated ref OR legacy string OR null) → name.
  const groupName = (g: AssetRow['dispatchGroup']): string => {
    if (!g) return '';
    if (typeof g === 'string') return g.trim();
    return (g.name ?? '').trim();
  };

  // Tab list = the master-data dispatch groups (so newly-added or renamed
  // groups appear/update instantly), unioned with any group names found on
  // visible assets but not yet in the master list (defensive — covers the
  // moment between a freshly imported asset and the next groups refetch).
  const dispatchGroups = React.useMemo(() => {
    const names = new Set<string>();
    for (const g of dispatchGroupRows ?? []) {
      if (g.name?.trim()) names.add(g.name.trim());
    }
    for (const a of allAssets) {
      const g = groupName(a.dispatchGroup);
      if (g) names.add(g);
    }
    return [...names].sort((x, y) => x.localeCompare(y));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchGroupRows, allAssets]);

  // Reset tab selection if the active group disappears from the dataset.
  React.useEffect(() => {
    if (activeGroup !== ALL_TAB && !dispatchGroups.includes(activeGroup)) {
      setActiveGroup(ALL_TAB);
    }
  }, [dispatchGroups, activeGroup]);

  const assets = React.useMemo(() => {
    let list = allAssets;
    if (activeGroup !== ALL_TAB) {
      list = list.filter((a) => groupName(a.dispatchGroup) === activeGroup);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((a) =>
        fillTemplate(CONFIG.assetRowTemplate, a).toLowerCase().includes(q)
        || (a.fleetNumber || '').toLowerCase().includes(q)
        || (a.registrationNumber || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [allAssets, search, activeGroup]);

  // Dispatch jobs whose [start, end] interval overlaps the visible range.
  // The naive `start in range` filter (what was here before) silently dropped
  // multi-day jobs on every day after their start — a 7-day job from 28/06
  // would only appear on 28/06 because its `start` fell outside subsequent
  // days' windows. The overlap form (`start <= rangeEnd AND end >= rangeStart`)
  // returns the job on every day it's active. Jobs without an `end` value still
  // show on their start day via the client-side fallback in `todaysJobs`.
  const { data: jobsResp } = useQuery({
    queryKey: ['/dispatch/jobs/schedule', rangeStart.toISOString(), rangeEnd.toISOString()],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_lte: rangeEnd.toISOString(),
        end_gte: rangeStart.toISOString(),
        limit: '5000',
      });
      const r = await api<JobRow[]>(`/dispatch/jobs?${params.toString()}`);
      return r.data || [];
    },
  });
  const jobs = jobsResp ?? [];

  // Group jobs by asset id for O(1) per-row rendering.
  const jobsByAsset = React.useMemo(() => {
    const m = new Map<string, JobRow[]>();
    for (const j of jobs) {
      const aid = resolveRef(j.asset);
      if (!aid) continue;
      if (!m.has(aid)) m.set(aid, []);
      m.get(aid)!.push(j);
    }
    return m;
  }, [jobs]);

  const hoursPerDay = CONFIG.gridEndHour - CONFIG.gridStartHour + 1;
  const gridWidth = hoursPerDay * HOUR_WIDTH * days;

  // NZ-local hour-of-day (fractional) for a Date, independent of viewer tz.
  const NZ_HOUR_PARTS_FMT = React.useMemo(() => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Pacific/Auckland', hour: '2-digit', minute: '2-digit', hour12: false,
  }), []);
  const nzHourFloat = React.useCallback((d: Date): number => {
    const parts = NZ_HOUR_PARTS_FMT.formatToParts(d);
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
    const h = get('hour');
    return (h === 24 ? 0 : h) + get('minute') / 60;
  }, [NZ_HOUR_PARTS_FMT]);

  // Given a job and the visible range, compute one or more positioned bars —
  // one per day column the job overlaps. Multi-day jobs span across day
  // boundaries via separate segments because off-hours (gridEndHour..gridStartHour
  // of the next day) aren't drawn on the grid. Returns [] when the job is
  // entirely outside the visible window.
  function jobSegments(job: JobRow): Array<{ left: number; width: number; isStart: boolean; isEnd: boolean }> {
    const s = job.start ? new Date(job.start) : null;
    const e = job.end ? new Date(job.end) : (s ? new Date(s.getTime() + 60 * 60 * 1000) : null);
    if (!s || !e) return [];
    // dayIndex for the start and end of the job relative to rangeStart.
    // startOfDay uses the JS runtime's local tz, which matches rangeStart's
    // local-midnight semantics — the relative day count is robust to viewer tz.
    const startDayIdx = Math.floor((startOfDay(s).getTime() - rangeStart.getTime()) / (24 * 60 * 60 * 1000));
    const endDayIdx   = Math.floor((startOfDay(e).getTime() - rangeStart.getTime()) / (24 * 60 * 60 * 1000));
    const firstVisible = Math.max(0, startDayIdx);
    const lastVisible  = Math.min(days - 1, endDayIdx);
    if (firstVisible > lastVisible) return [];
    const sHour = nzHourFloat(s);
    const eHour = nzHourFloat(e);
    const segments: Array<{ left: number; width: number; isStart: boolean; isEnd: boolean }> = [];
    for (let d = firstVisible; d <= lastVisible; d++) {
      // Per-segment time bounds: the actual job times on the start/end day,
      // gridStartHour/gridEndHour+1 for middle days the job fully spans.
      const segStart = d === startDayIdx ? sHour : CONFIG.gridStartHour;
      const segEnd   = d === endDayIdx   ? eHour : CONFIG.gridEndHour + 1;
      // Skip a segment whose visible hours don't intersect the grid window.
      if (segEnd < CONFIG.gridStartHour || segStart > CONFIG.gridEndHour + 1) continue;
      const clampedStart = Math.max(segStart, CONFIG.gridStartHour);
      const clampedEnd   = Math.min(segEnd, CONFIG.gridEndHour + 1);
      const dayOffsetPx = d * hoursPerDay * HOUR_WIDTH;
      const left = dayOffsetPx + (clampedStart - CONFIG.gridStartHour) * HOUR_WIDTH;
      const width = Math.max(28, (clampedEnd - clampedStart) * HOUR_WIDTH);
      segments.push({ left, width, isStart: d === startDayIdx, isEnd: d === endDayIdx });
    }
    return segments;
  }

  // Back-compat: callers that just need the FIRST segment's geometry (drag /
  // resize math operates on the start-day bar only — multi-day resize is a
  // larger UX problem we defer).
  function jobPosition(job: JobRow): { left: number; width: number } | null {
    const segs = jobSegments(job);
    return segs.length === 0 ? null : { left: segs[0].left, width: segs[0].width };
  }

  // Convert a row-relative X in px to an actual Date within the visible range.
  // Snaps to whole-hour blocks and clamps to the visible hours.
  const posToDate = React.useCallback((offsetX: number, mode: 'floor' | 'ceil' = 'floor'): Date => {
    const hoursFromStart = offsetX / HOUR_WIDTH;
    const snapFn = mode === 'ceil' ? Math.ceil : Math.floor;
    const snapped = snapFn(hoursFromStart);
    const clamped = Math.max(0, Math.min(hoursPerDay * days, snapped));
    const dayIdx = Math.min(days - 1, Math.floor(clamped / hoursPerDay));
    const hourInDay = clamped - dayIdx * hoursPerDay + CONFIG.gridStartHour;
    const d = new Date(addDays(rangeStart, dayIdx));
    d.setHours(hourInDay, 0, 0, 0);
    return d;
  }, [rangeStart, days, hoursPerDay]);

  const dragRef = React.useRef(drag);
  dragRef.current = drag;
  const rafRef = React.useRef<number | null>(null);

  /** Tears down the in-flight row gesture's window listeners. Held in a ref so
   *  unmount can cut them even though they are attached imperatively below. */
  const dragCleanupRef = React.useRef<(() => void) | null>(null);
  React.useEffect(() => () => dragCleanupRef.current?.(), []);

  function onRowPointerDown(e: React.PointerEvent<HTMLDivElement>, asset: AssetRow) {
    if (!canAddDispatchJob) return;
    // Primary button only — middle-click (button=1, autoscroll) and right-click
    // (button=2, context menu) should fall through to the browser. Without
    // this gate, a middle-click anywhere on the row would start a phantom
    // drag-to-create gesture. Touch and pen both report button=0, so they pass.
    if (e.button !== 0) return;
    // Secondary contacts of a multi-touch gesture (pinch-zoom, two-finger pan)
    // must not each start their own create.
    if (!e.isPrimary) return;
    // Pressing directly on a job bar should edit it, not start a drag.
    if ((e.target as HTMLElement).closest('[data-job-bar]')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pointerId = e.pointerId;
    setDrag({
      assetId: asset.id,
      startX: x,
      currentX: x,
      rowLeft: rect.left,
      pointerType: e.pointerType,
      startedAt: e.timeStamp,
    });
    // Mouse only: suppress text selection / native image drag while sweeping.
    // On touch this must NOT be called — it also cancels the browser's pan,
    // which is the gesture we are deliberately leaving alone (see row-gesture).
    if (e.pointerType === 'mouse') e.preventDefault();

    // Listeners are attached HERE rather than from an effect keyed on `drag`.
    // An effect only runs after React commits, and the browser can deliver the
    // entire down/up pair before that commit lands — which is exactly how the
    // previous mouse-event version lost taps on touch, where the synthetic
    // mousedown/mouseup arrive back-to-back after touchend. Attaching inline
    // guarantees the listener exists before the next event can arrive.
    const finish = () => {
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      dragCleanupRef.current = null;
    };
    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      const d = dragRef.current;
      if (!d) return;
      const nx = ev.clientX - d.rowLeft;
      // Throttle setState to animation frames so we re-render ≤60 Hz, not 1000 Hz.
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        setDrag((prev) => (prev ? { ...prev, currentX: nx } : prev));
      });
    };
    const up = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      const d = dragRef.current;
      finish();
      setDrag(null);
      if (!d) return;
      // Measure the release from the event, not from `d.currentX`: the rAF above
      // may not have run for the final move, so state can lag by a frame.
      const range = rowGestureRange({
        pointerType: d.pointerType,
        startX: d.startX,
        currentX: ev.clientX - d.rowLeft,
        startedAt: d.startedAt,
        endedAt: ev.timeStamp,
      });
      if (!range) return; // touch pan — leave the grid to scroll natively
      const start = posToDate(range.left, 'floor');
      const end = posToDate(range.right, 'ceil');
      if (end.getTime() <= start.getTime()) {
        end.setHours(start.getHours() + 1);
      }
      setNewJobDraft({ assetId: d.assetId, start, end });
    };
    const cancel = (ev: PointerEvent) => {
      // The browser claimed the contact (scroll/zoom won). No pointerup will
      // follow, so drop the gesture rather than latching `drag` forever.
      if (ev.pointerId !== pointerId) return;
      finish();
      setDrag(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    dragCleanupRef.current = finish;
  }

  // ---- Bar move + edge-resize ----

  // Mutation that PUTs the new start/end (and optionally a new asset) on
  // a dispatch job. The user has already confirmed via the pending-change
  // dialog by the time this fires, so we just write and re-fetch — no
  // optimistic patching (the dialog is the gate, not the toast).
  const updateJob = useMutation({
    mutationFn: async (args: { id: string; start?: Date | null; end?: Date | null; asset?: string | null }) => {
      const body: any = {};
      if (args.start !== undefined) body.start = args.start ? args.start.toISOString() : null;
      if (args.end !== undefined) body.end = args.end ? args.end.toISOString() : null;
      if (args.asset !== undefined) body.asset = args.asset;
      const r = await api(`/dispatch/jobs/${args.id}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Failed to update job');
    },
    // Success propagation (board, edit modal, driver views, other tabs) is
    // handled centrally by lib/data-sync.ts off the PUT itself. Failure is
    // the one case data-sync never sees (it only reports successful writes),
    // so re-read the board here to snap the bar back to the server's truth.
    onError: (e: any) => {
      toast('Move failed', e.message, 'error');
      qc.invalidateQueries({ queryKey: ['/dispatch/jobs/schedule'] });
    },
  });

  const interactionRef = React.useRef(interaction);
  interactionRef.current = interaction;
  const interactionRafRef = React.useRef<number | null>(null);
  /** Horizontal/vertical scroll container for the Gantt grid. Used by the
   *  auto-scroll-on-drag effect to advance the calendar when the user drags
   *  a job near a viewport edge (otherwise dragging Mon→Fri requires drop /
   *  scroll / re-drag). */
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  // Resolve which asset row sits beneath a given client point. Returns
  // `null` when the cursor is outside any row (e.g. on the day/hour
  // header) so we can reject the drop.
  function assetAtPoint(clientX: number, clientY: number): { id: string; rowLeft: number } | null {
    if (typeof document === 'undefined') return null;
    const el = document.elementFromPoint(clientX, clientY);
    if (!el) return null;
    const row = (el as HTMLElement).closest('[data-asset-row="1"]') as HTMLElement | null;
    if (!row) return null;
    const id = row.dataset.assetId;
    if (!id) return null;
    return { id, rowLeft: row.getBoundingClientRect().left };
  }

  React.useEffect(() => {
    if (!interaction) return;
    const move = (ev: PointerEvent) => {
      const it = interactionRef.current;
      if (!it) return;
      if (interactionRafRef.current != null) return;
      interactionRafRef.current = requestAnimationFrame(() => {
        interactionRafRef.current = null;
        setInteraction((prev) => {
          if (!prev) return prev;
          if (prev.kind === 'move') {
            const dx = Math.abs(ev.clientX - prev.startClientX);
            const dy = Math.abs(ev.clientY - prev.startClientY);
            const moved = prev.moved || dx > 3 || dy > 3;
            const hit = assetAtPoint(ev.clientX, ev.clientY);
            return {
              ...prev,
              clientX: ev.clientX,
              clientY: ev.clientY,
              targetAssetId: hit?.id ?? prev.targetAssetId,
              targetRowLeft: hit?.rowLeft ?? prev.targetRowLeft,
              moved,
            };
          }
          // resize
          const x = ev.clientX - prev.rowLeft;
          const moved = prev.moved || Math.abs(ev.clientX - prev.startClientX) > 3;
          return { ...prev, currentX: x, moved };
        });
      });
    };
    const up = (ev: PointerEvent) => {
      if (interactionRafRef.current != null) {
        cancelAnimationFrame(interactionRafRef.current); interactionRafRef.current = null;
      }
      const it = interactionRef.current;
      setInteraction(null);
      if (!it) return;
      // No movement → treat as click → open the edit dialog.
      if (!it.moved) { setEditingJobId(it.jobId); return; }
      const job = jobs.find((j) => j.id === it.jobId);
      if (!job) return;
      const jobLabel = job.jobNumber || job.info || 'Dispatch job';

      if (it.kind === 'move') {
        const hit = assetAtPoint(ev.clientX, ev.clientY);
        if (!hit) return; // dropped outside any row → cancel silently
        const offsetX = ev.clientX - hit.rowLeft - it.cursorOffsetX;
        const newStart = posToDate(offsetX, 'floor');
        const newEnd = new Date(newStart.getTime() + it.durationMs);
        const startedAt = new Date(it.origStartMs);
        const sameTime = newStart.getTime() === startedAt.getTime();
        const sameAsset = hit.id === it.origAssetId;
        // Tiny no-op gestures (drop where you started) shouldn't open
        // a confirm dialog at all.
        if (sameTime && sameAsset) return;
        const labelOf = (id: string) => {
          const a = assets.find((x) => x.id === id);
          return a ? fillTemplate(CONFIG.assetRowTemplate, a) : 'asset';
        };
        setPendingChange({
          kind: 'move',
          jobId: it.jobId,
          jobLabel,
          fromStart: startedAt,
          fromEnd: new Date(it.origStartMs + it.durationMs),
          toStart: newStart,
          toEnd: newEnd,
          fromAssetLabel: labelOf(it.origAssetId),
          toAssetLabel: labelOf(hit.id),
          nextAssetId: hit.id,
          assetChanged: !sameAsset,
        });
      } else {
        const x = ev.clientX - it.rowLeft;
        const fromStart = new Date(it.origStartMs);
        const fromEnd = new Date(it.origEndMs);
        if (it.kind === 'resize-start') {
          const proposed = posToDate(x, 'floor');
          const minStart = new Date(it.origEndMs - 60 * 60 * 1000); // ≥1h duration
          const toStart = proposed.getTime() > minStart.getTime() ? minStart : proposed;
          if (toStart.getTime() === it.origStartMs) return;
          setPendingChange({
            kind: 'resize', jobId: it.jobId, jobLabel, edge: 'start',
            fromStart, fromEnd, toStart, toEnd: fromEnd,
          });
        } else {
          const proposed = posToDate(x, 'ceil');
          const minEnd = new Date(it.origStartMs + 60 * 60 * 1000);
          const toEnd = proposed.getTime() < minEnd.getTime() ? minEnd : proposed;
          if (toEnd.getTime() === it.origEndMs) return;
          setPendingChange({
            kind: 'resize', jobId: it.jobId, jobLabel, edge: 'end',
            fromStart, fromEnd, toStart: fromStart, toEnd,
          });
        }
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!interaction]);

  // Auto-scroll the Gantt container while a `move` drag's cursor sits near
  // an edge — so dragging Mon→Fri (or scrolling the asset list mid-drag) is
  // a single gesture instead of drop/scroll/re-drag. Only runs for moves
  // because resize captures `rowLeft` at pointer-down and would mis-anchor
  // after a scroll; move resolves the drop row via hit-testing each frame.
  React.useEffect(() => {
    if (!interaction || interaction.kind !== 'move') return;
    const container = scrollRef.current;
    if (!container) return;
    const EDGE = 80;       // px from container edge where auto-scroll kicks in
    const MAX_SPEED = 18;  // px per frame at full lean
    let raf: number | null = null;
    const tick = () => {
      const it = interactionRef.current;
      if (!it || it.kind !== 'move') { raf = null; return; }
      const rect = container.getBoundingClientRect();
      const x = it.clientX;
      const y = it.clientY;
      let dx = 0, dy = 0;
      if (x < rect.left + EDGE)        dx = -((rect.left + EDGE - x) / EDGE) * MAX_SPEED;
      else if (x > rect.right - EDGE)  dx =  ((x - (rect.right - EDGE)) / EDGE) * MAX_SPEED;
      if (y < rect.top + EDGE)         dy = -((rect.top + EDGE - y) / EDGE) * MAX_SPEED;
      else if (y > rect.bottom - EDGE) dy =  ((y - (rect.bottom - EDGE)) / EDGE) * MAX_SPEED;
      // Clamp — past the edge (negative distance) the formula otherwise
      // outpaces a single tick and snaps the calendar across.
      dx = Math.max(-MAX_SPEED, Math.min(MAX_SPEED, dx));
      dy = Math.max(-MAX_SPEED, Math.min(MAX_SPEED, dy));
      if (dx) container.scrollLeft += dx;
      if (dy) container.scrollTop  += dy;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf != null) cancelAnimationFrame(raf); };
  }, [interaction?.kind === 'move']);

  function onBarPointerDown(e: React.PointerEvent<HTMLDivElement>, j: JobRow, assetId: string) {
    // Right / middle button never drags — right-click opens the context menu
    // (handled by onContextMenu), so bail before starting a move/resize.
    if (e.button !== 0) return;
    if (!canAddDispatchJob) return;
    e.stopPropagation();
    e.preventDefault();
    const bar = e.currentTarget;
    const barRect = bar.getBoundingClientRect();
    const rowEl = bar.parentElement as HTMLElement;
    const rowRect = rowEl.getBoundingClientRect();
    const xWithinBar = e.clientX - barRect.left;
    const HANDLE = 6;
    const startMs = j.start ? new Date(j.start).getTime() : 0;
    const endMs = j.end ? new Date(j.end).getTime() : (startMs + 60 * 60 * 1000);
    if (!startMs) return;
    if (xWithinBar <= HANDLE) {
      setInteraction({
        kind: 'resize-start', jobId: j.id, assetId,
        origStartMs: startMs, origEndMs: endMs,
        rowLeft: rowRect.left,
        startClientX: e.clientX,
        currentX: e.clientX - rowRect.left,
        moved: false,
      });
    } else if (xWithinBar >= barRect.width - HANDLE) {
      setInteraction({
        kind: 'resize-end', jobId: j.id, assetId,
        origStartMs: startMs, origEndMs: endMs,
        rowLeft: rowRect.left,
        startClientX: e.clientX,
        currentX: e.clientX - rowRect.left,
        moved: false,
      });
    } else {
      setInteraction({
        kind: 'move', jobId: j.id, origAssetId: assetId,
        origStartMs: startMs, durationMs: endMs - startMs,
        cursorOffsetX: e.clientX - barRect.left,
        startClientX: e.clientX, startClientY: e.clientY,
        clientX: e.clientX, clientY: e.clientY,
        targetAssetId: assetId,
        targetRowLeft: rowRect.left,
        moved: false,
      });
    }
  }

  // Was a local two-status map; now shared with the driver page and the run
  // sheet so the hues cannot drift apart. See scheduleBarClass for why grey
  // no longer doubles as "status we don't recognise" and "En route".
  const statusColor = scheduleBarClass;

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <h1 className="text-xl md:text-2xl font-semibold">All Dispatches</h1>
        {/* Button shrinks on mobile and abbreviates to "Run Sheet" so the
            title doesn't wrap to two lines under it. */}
        <Button
          variant="destructive"
          size="sm"
          className="md:h-10 md:px-4 md:text-sm shrink-0 gap-1.5"
          onClick={() => setGenOpen(true)}
        >
          <Plus className="h-4 w-4" />
          <span className="md:hidden">Run Sheet</span>
          <span className="hidden md:inline">Generate Run Sheet</span>
        </Button>
      </div>

      {/* Desktop control bar — Week/Day toggle, date display, search, prev /
          Today / next, date picker. Hidden on mobile because MobileSchedule
          below has its own equivalents (forced-Day mode, prev/Today/next,
          and the day label). Search is broken out into its own mobile-only
          row so asset filtering still works on phones. */}
      <div className="hidden lg:flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-muted rounded-md p-1">
          <Button size="sm" variant={view === 'week' ? 'default' : 'ghost'} onClick={() => setView('week')}>Week</Button>
          <Button size="sm" variant={view === 'day' ? 'default' : 'ghost'} onClick={() => setView('day')}>Day</Button>
        </div>

        <div className="text-sm font-medium">
          {view === 'week'
            ? fmtDateRange(rangeStart, addDays(rangeEnd, -1))
            : fmtNz(rangeStart, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Find asset..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 w-48"
            />
          </div>
          <Button aria-label="Previous period" size="icon" variant="outline" onClick={() => setAnchor(addDays(anchor, -days))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAnchor(view === 'week' ? startOfWeek(new Date()) : startOfDay(new Date()))}>Today</Button>
          <Button aria-label="Next period" size="icon" variant="outline" onClick={() => setAnchor(addDays(anchor, days))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Input
            aria-label="Jump to date"
            type="date"
            value={toDateInputValue(rangeStart)}
            onChange={(e) => {
              if (!e.target.value) return;
              const picked = parseNzWallToUtc(`${e.target.value}T00:00`);
              if (!picked) return;
              setAnchor(view === 'week' ? startOfWeek(picked) : startOfDay(picked));
            }}
            className="h-9 w-40"
          />
        </div>
      </div>

      {/* Mobile-only: full-width search. MobileSchedule already provides the
          date nav and Day/List toggle, so search is the only desktop control
          worth keeping on phones. */}
      <div className="relative lg:hidden">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Find asset..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 h-9 w-full"
        />
      </div>

      {/* Mobile: dispatch-group picker collapses into a single Select so the
          dispatch chrome stays compact regardless of how many groups exist. */}
      <div className="lg:hidden">
        <Select value={activeGroup} onValueChange={setActiveGroup}>
          <SelectTrigger className="h-9 w-full">
            <SelectValue placeholder="All groups" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TAB}>All</SelectItem>
            {dispatchGroups.map((g) => (
              <SelectItem key={g} value={g}>{g}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={activeGroup} onValueChange={setActiveGroup} className="hidden lg:block">
        {/* Desktop: keeps the wrap-to-multiple-rows tab layout so all groups
            are visible at a glance with no scrolling or extra interaction. */}
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value={ALL_TAB}>All</TabsTrigger>
          {dispatchGroups.map((g) => (
            <TabsTrigger key={g} value={g}>{g}</TabsTrigger>
          ))}
        </TabsList>
        {/* Empty TabsContent panels keep the aria-controls on each
            TabsTrigger pointing at a real DOM node — without them, axe
            (correctly) flags `aria-controls` as referencing a non-existent
            element. The tabs filter the Gantt grid below; they don't
            actually have separate content panels. */}
        <TabsContent value={ALL_TAB} className="hidden" />
        {dispatchGroups.map((g) => (
          <TabsContent key={g} value={g} className="hidden" />
        ))}
      </Tabs>

      {/* Mobile/tablet-portrait dispatch view (Day or List) — replaces the
          Gantt below md. Reuses the same `assets`, `jobs`, `anchor` state
          and tapping a job opens the existing edit dialog. */}
      <MobileSchedule
        assets={assets}
        jobs={jobs}
        anchor={anchor}
        setAnchor={setAnchor}
        onJobTap={(id) => setEditingJobId(id)}
        onAddJob={canAddDispatchJob ? (assetId, hour) => {
          // Prefill the New Job dialog at the tapped hour; reuse the same
          // drag-create flow by seeding a synthetic 1-hour drag.
          const startHour = Math.max(CONFIG.gridStartHour, Math.min(CONFIG.gridEndHour, hour));
          const startDate = new Date(anchor);
          startDate.setHours(Math.floor(startHour), Math.round((startHour % 1) * 60), 0, 0);
          const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
          setNewJobDraft({ assetId, start: startDate, end: endDate });
        } : undefined}
      />

      <Card className="hidden lg:block w-full overflow-hidden">
        <CardContent ref={scrollRef} className="p-0 overflow-x-auto overflow-y-auto max-h-[calc(100vh-220px)]">
          <div style={{ width: ASSET_COL_WIDTH + gridWidth, minWidth: '100%' }}>
            {/* Header: day labels + hour labels — sticky top so they stay while scrolling rows */}
            <div className="flex sticky top-0 bg-background z-20 border-b">
              <div
                style={{ width: ASSET_COL_WIDTH }}
                className="shrink-0 sticky left-0 z-30 flex items-center px-3 py-2 text-sm font-medium border-r bg-background shadow-[1px_0_0_hsl(var(--border))]"
              >
                Vehicle
              </div>
              <div style={{ width: gridWidth }} className="flex flex-col">
                {/* Day band */}
                <div className="flex border-b">
                  {Array.from({ length: days }).map((_, dIdx) => {
                    const d = addDays(rangeStart, dIdx);
                    return (
                      <div
                        key={dIdx}
                        style={{ width: hoursPerDay * HOUR_WIDTH }}
                        className="text-center text-sm font-medium py-1 border-r last:border-r-0"
                      >
                        {fmtShort(d)}
                      </div>
                    );
                  })}
                </div>
                {/* Hour band */}
                <div className="flex text-[10px] text-muted-foreground">
                  {Array.from({ length: days }).map((_, dIdx) => (
                    <div key={dIdx} style={{ width: hoursPerDay * HOUR_WIDTH }} className="flex border-r last:border-r-0">
                      {Array.from({ length: hoursPerDay }).map((__, hIdx) => (
                        <div
                          key={hIdx}
                          style={{ width: HOUR_WIDTH }}
                          className="text-center py-1"
                        >
                          {String(CONFIG.gridStartHour + hIdx).padStart(2, '0')}:00
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Rows */}
            {assets.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No records.
              </div>
            )}
            {assets.map((a) => {
              const rowJobs = jobsByAsset.get(a.id) ?? [];
              return (
                <div key={a.id} className="flex border-b hover:bg-muted/20" style={{ height: ROW_HEIGHT }}>
                  <div
                    style={{ width: ASSET_COL_WIDTH }}
                    className="shrink-0 sticky left-0 z-10 bg-background flex items-center px-3 text-xs font-medium border-r truncate shadow-[1px_0_0_hsl(var(--border))]"
                  >
                    {fillTemplate(CONFIG.assetRowTemplate, a)}
                  </div>
                  <div
                    data-asset-row="1"
                    data-asset-id={a.id}
                    style={{
                      width: gridWidth,
                      position: 'relative',
                      // Hour-tick lines via CSS gradients (one layer) + day separator (bolder).
                      backgroundImage: `
                        linear-gradient(to right, hsl(var(--border)) 0 1px, transparent 1px ${HOUR_WIDTH}px),
                        linear-gradient(to right, hsl(var(--border)) 0 1px, transparent 1px ${hoursPerDay * HOUR_WIDTH}px)
                      `,
                      backgroundSize: `${HOUR_WIDTH}px 100%, ${hoursPerDay * HOUR_WIDTH}px 100%`,
                      backgroundRepeat: 'repeat, repeat',
                    }}
                    className="select-none"
                    onPointerDown={(e) => onRowPointerDown(e, a)}
                  >
                    {/* Job bars, absolutely positioned. Multi-day jobs emit
                        one bar per visible day they span; the FIRST segment
                        (the start-day bar) owns drag / resize interaction,
                        subsequent segments are visual continuations. */}
                    {rowJobs.flatMap((j) => {
                      const segs = jobSegments(j);
                      if (segs.length === 0) return [];
                      const title = (j as any)[CONFIG.barTitleField] || j.info || j.jobNumber || '—';
                      const isInteracting = interaction?.jobId === j.id;
                      return segs.map((seg, segIdx) => {
                        const isPrimary = segIdx === 0;
                        // Live preview offsets — only the primary segment
                        // takes the drag/resize geometry. Other segments
                        // dim during a 'move' so the job reads as one unit.
                        let liveLeft = seg.left + 1;
                        let liveWidth = seg.width - 2;
                        let dimmed = false;
                        if (isInteracting && interaction!.moved) {
                          if (interaction!.kind === 'move') {
                            // Hide ALL segments — the floating preview
                            // (below) represents the whole job.
                            dimmed = true;
                          } else if (isPrimary && interaction!.kind === 'resize-start') {
                            const x = interaction!.currentX;
                            const minLeft = seg.left + seg.width - HOUR_WIDTH;
                            const newLeft = Math.min(minLeft, Math.floor(x / HOUR_WIDTH) * HOUR_WIDTH);
                            liveLeft = newLeft + 1;
                            liveWidth = (seg.left + seg.width) - newLeft - 2;
                          } else if (isPrimary) {
                            const x = interaction!.currentX;
                            const minRight = seg.left + HOUR_WIDTH;
                            const newRight = Math.max(minRight, Math.ceil(x / HOUR_WIDTH) * HOUR_WIDTH);
                            liveWidth = newRight - seg.left - 2;
                          }
                        }
                        return (
                          <div
                            key={`${j.id}-${segIdx}`}
                            data-job-bar
                            role="button"
                            tabIndex={0}
                            aria-label={`Dispatch job ${j.jobNumber ?? ''}. Drag to move, drag edges to resize. Press Enter to edit.`}
                            className={`absolute text-[11px] px-1.5 py-0.5 rounded border truncate shadow-sm cursor-grab active:cursor-grabbing ${statusColor(j.status)} ${dimmed ? 'opacity-30' : ''}`}
                            style={{ left: liveLeft, width: Math.max(20, liveWidth), top: 6, height: ROW_HEIGHT - 12 }}
                            title={`${j.jobNumber ?? ''} · ${j.customer ?? ''} · ${j.info ?? ''}`}
                            onPointerDown={(e) => onBarPointerDown(e, j, a.id)}
                            onContextMenu={canAddDispatchJob ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setJobMenu({ jobId: j.id, x: e.clientX, y: e.clientY });
                            } : undefined}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingJobId(j.id); } }}
                          >
                            {/* Resize handles — only on the segment edges that
                                match the actual job boundary. A middle-day
                                "all-day" segment can't be resized to a
                                meaningful different time. */}
                            {seg.isStart && (
                              <span aria-hidden className="absolute inset-y-0 left-0 w-1.5 cursor-ew-resize" />
                            )}
                            {seg.isEnd && (
                              <span aria-hidden className="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize" />
                            )}
                            {title}
                          </div>
                        );
                      });
                    })}
                    {/* Move preview — shown in the target row at the
                        snapped destination. Width = original duration. */}
                    {interaction?.kind === 'move'
                      && interaction.moved
                      && interaction.targetAssetId === a.id
                      && (() => {
                        const offsetX = interaction.clientX - interaction.targetRowLeft - interaction.cursorOffsetX;
                        const snappedLeft = Math.max(0, Math.floor(offsetX / HOUR_WIDTH) * HOUR_WIDTH);
                        const widthHours = interaction.durationMs / 3_600_000;
                        const widthPx = Math.max(HOUR_WIDTH, Math.round(widthHours * HOUR_WIDTH));
                        return (
                          <div
                            className="absolute pointer-events-none rounded border-2 border-dashed border-primary bg-primary/15"
                            style={{ left: snappedLeft, width: widthPx, top: 6, height: ROW_HEIGHT - 12 }}
                          />
                        );
                      })()}
                    {/* Drag-to-create ghost bar — snapped to whole-hour blocks.
                        Mouse only: a touch still in flight is indistinguishable
                        from the start of a pan, so a ghost would flash on every
                        scroll of the board. */}
                    {drag?.assetId === a.id && showsGhost(drag.pointerType) && (() => {
                      const rawLeft = Math.min(drag.startX, drag.currentX);
                      const rawRight = Math.max(drag.startX, drag.currentX);
                      const snappedLeft = Math.floor(rawLeft / HOUR_WIDTH) * HOUR_WIDTH;
                      const snappedRight = Math.max(
                        snappedLeft + HOUR_WIDTH,
                        Math.ceil(rawRight / HOUR_WIDTH) * HOUR_WIDTH,
                      );
                      return (
                        <div
                          className="absolute border-2 border-dashed border-primary bg-primary/15 pointer-events-none rounded"
                          style={{
                            left: snappedLeft,
                            width: snappedRight - snappedLeft,
                            top: 6,
                            height: ROW_HEIGHT - 12,
                          }}
                        />
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Confirm-on-drop dialog. The drag/resize gesture stages a
          PendingChange — nothing is written until the user confirms.
          Cancel closes the dialog and the bar stays where it was. */}
      {pendingChange && (
        <Dialog open onOpenChange={(o) => { if (!o) setPendingChange(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {pendingChange.kind === 'move'
                  ? (pendingChange.assetChanged ? 'Reassign job?' : 'Move job?')
                  : 'Resize job?'}
              </DialogTitle>
            </DialogHeader>
            {pendingChange.kind === 'move' ? (
              <div className="text-sm space-y-2">
                <div>
                  <span className="text-muted-foreground">Job: </span>
                  <span className="font-medium">{pendingChange.jobLabel}</span>
                </div>
                {pendingChange.assetChanged && (
                  <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                    <span className="text-muted-foreground">From asset</span>
                    <span className="font-medium">{pendingChange.fromAssetLabel}</span>
                    <span className="text-muted-foreground">To asset</span>
                    <span className="font-medium">{pendingChange.toAssetLabel}</span>
                  </div>
                )}
                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  <span className="text-muted-foreground">From</span>
                  <span>
                    {fmtDateTime(pendingChange.fromStart)}
                    {' – '}
                    {pendingChange.fromEnd.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Pacific/Auckland', hour12: false })}
                  </span>
                  <span className="text-muted-foreground">To</span>
                  <span className="font-medium">
                    {fmtDateTime(pendingChange.toStart)}
                    {' – '}
                    {pendingChange.toEnd.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Pacific/Auckland', hour12: false })}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-sm space-y-2">
                <div>
                  <span className="text-muted-foreground">Job: </span>
                  <span className="font-medium">{pendingChange.jobLabel}</span>
                </div>
                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  <span className="text-muted-foreground">Was</span>
                  <span>
                    {pendingChange.fromStart.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Pacific/Auckland', hour12: false })}
                    {' – '}
                    {pendingChange.fromEnd.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Pacific/Auckland', hour12: false })}
                  </span>
                  <span className="text-muted-foreground">Now</span>
                  <span className="font-medium">
                    {pendingChange.toStart.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Pacific/Auckland', hour12: false })}
                    {' – '}
                    {pendingChange.toEnd.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Pacific/Auckland', hour12: false })}
                  </span>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPendingChange(null)}>Cancel</Button>
              <Button
                disabled={updateJob.isPending}
                onClick={() => {
                  const c = pendingChange;
                  if (c.kind === 'move') {
                    updateJob.mutate({
                      id: c.jobId,
                      start: c.toStart,
                      end: c.toEnd,
                      asset: c.assetChanged ? c.nextAssetId : undefined,
                    }, {
                      onSuccess: () => setPendingChange(null),
                    });
                  } else {
                    updateJob.mutate({
                      id: c.jobId,
                      start: c.edge === 'start' ? c.toStart : undefined,
                      end: c.edge === 'end' ? c.toEnd : undefined,
                    }, {
                      onSuccess: () => setPendingChange(null),
                    });
                  }
                }}
              >
                {updateJob.isPending ? 'Saving…' : 'Confirm'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {genOpen && (
        <GenerateRunSheetDialog
          defaultDate={new Date()}
          onClose={() => setGenOpen(false)}
          onCreated={() => {
            setGenOpen(false);
            qc.invalidateQueries({ queryKey: ['/run-sheets'] });
          }}
        />
      )}

      {editingJobId && (
        <EditDispatchJobDialog
          key={editingJobId}
          jobId={editingJobId}
          onClose={() => setEditingJobId(null)}
          onSaved={() => setEditingJobId(null)}
          onDuplicate={(id) => { setEditingJobId(null); setDuplicatingJobId(id); }}
        />
      )}

      {newJobDraft && (
        <CreateDispatchJobDialog
          draft={newJobDraft}
          assetLabel={(() => {
            const a = allAssets.find((x) => x.id === newJobDraft.assetId);
            return a ? fillTemplate(CONFIG.assetRowTemplate, a) : newJobDraft.assetId;
          })()}
          onClose={() => setNewJobDraft(null)}
          onCreated={() => setNewJobDraft(null)}
        />
      )}

      {duplicatingJobId && (
        <DuplicateDispatchJobDialog
          key={duplicatingJobId}
          jobId={duplicatingJobId}
          onClose={() => setDuplicatingJobId(null)}
          onCreated={() => setDuplicatingJobId(null)}
        />
      )}

      {/* Right-click context menu for a job bar. Lightweight, pointer-anchored;
          clamped so it never spills past the right/bottom viewport edge. */}
      {jobMenu && (
        <div
          role="menu"
          aria-label="Job actions"
          className="fixed z-50 min-w-[160px] rounded-md border bg-popover text-popover-foreground p-1 shadow-md"
          style={{
            top: Math.min(jobMenu.y, window.innerHeight - 60),
            left: Math.min(jobMenu.x, window.innerWidth - 176),
          }}
        >
          <button
            type="button"
            role="menuitem"
            className="w-full text-left text-sm px-2 py-1.5 rounded-sm hover:bg-accent hover:text-accent-foreground"
            onClick={() => { setDuplicatingJobId(jobMenu.jobId); setJobMenu(null); }}
          >
            Duplicate
          </button>
        </div>
      )}
    </div>
  );
}

// Staged product line item — held in component state until the parent job is created,
// then POSTed in a follow-up batch with the new dispatchJob id attached.
interface StagedProduct {
  catalogueItem: string;
  itemCode?: string;
  description?: string;
  quantity: number;
  unit?: string;
}

function CreateDispatchJobDialog({
  draft, assetLabel, onClose, onCreated,
}: {
  draft: { assetId: string; start: Date; end: Date };
  assetLabel: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  // Status is fixed to SCHEDULED on create — only the "Mark Complete" action flips it.
  // Info is auto-derived server-side from customer + product item codes (no manual input).
  const [customerPick, setCustomerPick] = React.useState<CustomerPickerValue>({ excludeFromWeightrax: false, site: null, customerRef: null, customer: null, newCustomer: null });
  const customerError = customerPickerError(customerPick);
  // Seeded from the draft. Stays fixed for row-originated creates (the row IS
  // the vehicle); user-settable only when the draft arrived without one.
  const [assetId, setAssetId] = React.useState<string>(draft.assetId);
  // NzDateTimeInput holds Date | null directly — the picker handles NZ tz +
  // 24-hour formatting itself.
  const [start, setStart] = React.useState<Date | null>(draft.start);
  const [end, setEnd] = React.useState<Date | null>(draft.end);
  // Aligned with the Dispatch Jobs CRUD form so a calendar-created job
  // is no longer a stub — same field set as a /dispatch/jobs "Add" entry.
  const [jobNumber, setJobNumber] = React.useState('');
  const [jobDetails, setJobDetails] = React.useState('');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [driverId, setDriverId] = React.useState<string>('');
  // Recurring controls — when ON the server's daily 00:01 tick will spawn
  // a copy each cron-fire datetime. Default cron = 06:00 weekdays.
  const [recurring, setRecurring] = React.useState(false);
  const [cron, setCron] = React.useState('0 6 * * 1-5');
  // Block "Create" when the recurring + duration combo would produce
  // overlapping spawns. Same rule the edit dialog enforces; sourced from
  // the shared validator so client + server agree on the cutoff.
  const newJobRecurrenceError = React.useMemo(
    () => recurring ? validateRecurringDuration(start, end, cron, 'Pacific/Auckland') : null,
    [recurring, start, end, cron],
  );
  // End must be after Start — same shared check as the edit dialog / server.
  const newJobTimeError = React.useMemo(() => validateTimeRange(start, end), [start, end]);
  // Allocate-on-open: the moment this dialog mounts we atomically bump
  // the server-side counter and lock the resulting number to THIS
  // dialog. Another dispatcher opening the form concurrently gets the
  // next number, so the value the user sees never silently shifts under
  // them. Cancelled dialogs leak the number (sequence gap) — same UX
  // as deletion-induced gaps, simpler than a reservation table.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await api<{ jobNumber: string }>('/dispatch/jobs/allocate-number', { method: 'POST' });
      if (!cancelled && r.success && r.data?.jobNumber) setJobNumber(r.data.jobNumber);
    })();
    return () => { cancelled = true; };
  }, []);
  // Driver options fetched server-side by the LookupField below using the
  // shared EMPLOYEE_REF_QUERY filter (active only + alphabetical).
  const [address, setAddress] = React.useState<{
    addressLine1?: string; addressLine2?: string; city?: string;
    stateProvince?: string; postalCode?: string; country?: string;
  }>({});
  const addressLine1 = address.addressLine1 ?? '';

  // Staged products + catalogue picker state.
  // Tracks the full picked CatalogueItem so addStaged can carry the
  // itemCode + description forward without re-fetching by id (the
  // LookupField fires `onPick` with the row it just resolved).
  const [products, setProducts] = React.useState<StagedProduct[]>([]);
  const [newItem, setNewItem] = React.useState<any | null>(null);
  const [newQty, setNewQty] = React.useState('');
  const [newUnit, setNewUnit] = React.useState('');
  // Pickable units (managed at /master-data/units). Mirrors the
  // Dispatch Jobs CRUD subform so both create paths offer the same
  // curated unit list instead of a free-text Input.
  const { data: units = [] } = useQuery<any[]>({
    queryKey: ['/master-data/units'],
    queryFn: async () => (await api<any[]>('/master-data/units?limit=200')).data || [],
  });

  function addStaged() {
    if (!newItem || !newQty) return;
    setProducts((cur) => [...cur, {
      catalogueItem: newItem.id,
      itemCode: newItem.itemCode,
      description: newItem.description,
      quantity: Number(newQty),
      unit: newUnit || undefined,
    }]);
    setNewItem(null); setNewQty(''); setNewUnit('');
  }

  const create = useMutation({
    mutationFn: async () => {
      // Send `address` only when the user actually entered something —
      // an empty subform persists as null to keep list views clean.
      const hasAnyAddress = Object.values(address).some((v) => v && String(v).trim());
      const body = {
        asset: assetId,
        status: SCHEDULED,
        ...customerPickerPayload(customerPick),
        start: start ? start.toISOString() : null,
        end: end ? end.toISOString() : null,
        jobNumber: jobNumber.trim() || null,
        jobDetails: jobDetails.trim() || null,
        phoneNumber: phoneNumber.trim() || null,
        driver: driverId || null,
        address: hasAnyAddress ? address : null,
        recurring,
        recurrencePattern: recurring ? { cron, timezone: 'Pacific/Auckland' } : null,
      };
      const r = await api<any>('/dispatch/jobs', { method: 'POST', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Create failed');
      const newId = r.data?.id || r.data?._id;
      // Persist staged products against the freshly-created job.
      if (newId && products.length > 0) {
        for (const p of products) {
          const pr = await api('/dispatch-job-products', {
            method: 'POST',
            body: JSON.stringify({
              dispatchJob: newId,
              catalogueItem: p.catalogueItem,
              quantity: p.quantity,
              unit: p.unit ?? null,
            }),
          });
          if (!pr.success) throw new Error(pr.error?.message || 'Failed to add product');
        }
      }
    },
    onSuccess: () => { toast('Dispatch job created', undefined, 'success'); onCreated(); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent hideClose className={FULLSCREEN_DIALOG_CLASS}>
        {/* `sticky` below lg, not `shrink-0`: now that the whole dialog scrolls
            (see the body), the title would scroll away with it and the user would
            lose track of which job they're editing. Sticky keeps that context
            without pinning the footer too.
            `data-sticky-top` tells the keyboard-follow maths that this bar covers
            the top of the scrollport, so a focused field is never scrolled up
            behind it — without it, the field's own label disappears under here. */}
        <DialogHeader data-sticky-top className="max-lg:sticky max-lg:top-0 max-lg:z-10 max-lg:bg-background relative px-4 py-3 pr-12 border-b shrink-0 lg:p-0 lg:pr-12 lg:border-0 lg:pb-2">
          <DialogTitle className="text-base lg:text-lg">New Dispatch Job</DialogTitle>
          {/* The X lives in the sticky bar, not in DialogContent (hence
              `hideClose`): the default one is positioned against the content
              box, so once the dialog scrolls as one piece it scrolls out of
              reach. Here it stays pinned with the title. */}
          <DialogClose className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        {/* Below lg the body is NOT its own scroller: the whole dialog scrolls as
            one piece, so the footer flows after the content instead of being
            pinned above the keyboard. `flex-none` + `overflow-visible` hand the
            scrolling back to DialogContent. At lg+ the inner scroller returns, so
            the desktop modal keeps its fixed height with the actions in view. */}
        <div className="max-lg:flex-none max-lg:overflow-visible flex-1 overflow-y-auto px-4 py-4 space-y-4 text-sm lg:px-0 lg:py-0 lg:max-h-[70vh] lg:space-y-3">
          <div>
            <Label>Asset</Label>
            {/* The Gantt grid (and the single-vehicle mobile row) identify the
                asset from the row itself, so it shows read-only. A mobile hour
                row carries no vehicle — then this becomes a picker so the one
                dialog still completes the job, rather than punting the user to
                a separate pre-dialog. */}
            {draft.assetId ? (
              <Input value={assetLabel} readOnly className="bg-muted/40 h-11 lg:h-9" />
            ) : (
              <LookupField
                endpoint="/fleet/assets"
                labelKey="fleetNumber"
                secondaryLabelKey="registrationNumber"
                searchAllFields
                value={assetId || null}
                onChange={(id) => setAssetId(id ?? '')}
                placeholder="Pick a vehicle…"
              />
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-2">
            <div><Label>Start</Label><NzDateTimeInput ariaLabel="Start" withTime value={start} onChange={setStart} /></div>
            <div><Label>End</Label><NzDateTimeInput ariaLabel="End" withTime value={end} onChange={setEnd} /></div>
          </div>
          {newJobTimeError && (
            <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {newJobTimeError}
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-2">
            <div><Label>Job Number</Label><Input className="h-11 lg:h-9 bg-muted/40" value={jobNumber} readOnly placeholder="Allocating…" title="Auto-allocated when the dialog opens. Cancelled dialogs leak the number (sequence gap)." /></div>
            <CustomerPicker value={customerPick} onChange={setCustomerPick} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-2">
            <div><Label>Phone</Label><Input className="h-11 lg:h-9" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} /></div>
            <div>
              <Label>Driver</Label>
              <LookupField
                endpoint="/employees"
                labelKey="fullName"
                filter={EMPLOYEE_REF_QUERY}
                value={driverId || null}
                onChange={(id) => setDriverId(id ?? '')}
                placeholder="— Select driver —"
              />
            </div>
          </div>
          <div><Label>Job Details</Label><Input aria-label="Job Details" className="h-11 lg:h-9" value={jobDetails} onChange={(e) => setJobDetails(e.target.value)} /></div>
          <div>
            <Label>Address</Label>
            {/* Same Mapbox autocomplete the CRUD form uses — typing into
                Line 1 cascades the parsed parts (city, state, postcode,
                country) into the structured address object. */}
            <AddressAutocomplete
              value={addressLine1}
              onChange={(v) => setAddress((cur) => ({ ...cur, addressLine1: v }))}
              onSelect={(feat) => {
                // Show the full picked address in the input; keep the
                // structured parts (line2/city/region/postcode/country)
                // behind the scenes so they still get saved.
                const parts = extractAddressParts(feat);
                setAddress((cur) => ({ ...cur, ...parts, addressLine1: feat.place_name }));
              }}
              placeholder="Start typing an address…"
              ariaLabel="Job address"
            />
          </div>

          {/* Recurring — "baton-pass" model. When ON, the server's daily 00:01
              tick spawns a copy each cron-fire datetime and flips this job's
              recurring back off; the spawned copy becomes the new chain head.
              Hidden behind RECURRING_UI_ENABLED — superseded by Duplicate. */}
          {RECURRING_UI_ENABLED && (
            <div className="border-t pt-4 lg:pt-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="recurring-toggle" className="cursor-pointer">Recurring</Label>
                <Switch id="recurring-toggle" checked={recurring} onCheckedChange={setRecurring} />
              </div>
              {recurring && newJobRecurrenceError && (
                <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                  {newJobRecurrenceError}
                </div>
              )}
              {recurring && <ScheduleBuilder cron={cron} onChange={setCron} allowCustom={false} />}
            </div>
          )}

          {/* Staged product line items — saved with the job on create. */}
          <div className="border-t pt-4 lg:pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label>Product line items ({products.length})</Label>
            </div>
            {products.length === 0 && (
              <div className="text-xs text-muted-foreground py-2">No products added yet.</div>
            )}
            {products.length > 0 && (
              <>
                <div className="lg:hidden space-y-2">
                  {products.map((p, i) => (
                    <div key={i} className="border rounded-md p-3 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{p.itemCode ?? '—'}</div>
                        {p.description && <div className="text-xs text-muted-foreground truncate">{p.description}</div>}
                        <div className="text-xs text-muted-foreground mt-1">
                          Qty: <span className="text-foreground">{p.quantity}</span>
                          {p.unit ? <> · {p.unit}</> : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-destructive text-lg leading-none px-2 py-1"
                        onClick={() => setProducts((cur) => cur.filter((_, j) => j !== i))}
                        aria-label="Remove product"
                      >×</button>
                    </div>
                  ))}
                </div>
                <table className="hidden lg:table w-full text-xs border rounded overflow-hidden">
                  <thead className="bg-muted/40 text-[11px]">
                    <tr>
                      <th className="text-left px-2 py-1">Item</th>
                      <th className="text-left px-2 py-1">Description</th>
                      <th className="text-right px-2 py-1 w-16">Qty</th>
                      <th className="text-left px-2 py-1 w-16">Unit</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1">{p.itemCode ?? '—'}</td>
                        <td className="px-2 py-1 text-muted-foreground">{p.description ?? ''}</td>
                        <td className="px-2 py-1 text-right">{p.quantity}</td>
                        <td className="px-2 py-1">{p.unit ?? ''}</td>
                        <td className="px-2 py-1">
                          <button
                            className="text-destructive hover:underline text-xs"
                            onClick={() => setProducts((cur) => cur.filter((_, j) => j !== i))}
                            type="button"
                          >×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            <div className="mt-3 grid grid-cols-1 gap-2 lg:mt-2 lg:grid-cols-[1fr_80px_80px_auto] lg:gap-1">
              <LookupField
                endpoint="/catalogue-items"
                labelKey="itemCode"
                secondaryLabelKey="description"
                searchAllFields
                placeholder="Catalogue item…"
                className="h-11 text-sm lg:h-8 lg:text-xs"
                value={newItem?.id ?? null}
                onChange={(id) => { if (!id) setNewItem(null); }}
                onPick={(item) => {
                  setNewItem(item);
                  if (item?.unit) setNewUnit(item.unit);
                }}
              />
              <div className="grid grid-cols-2 gap-2 lg:contents">
                <Input className="h-11 text-sm lg:h-8 lg:text-xs" type="number" step="0.01" placeholder="Qty" value={newQty} onChange={(e) => setNewQty(e.target.value)} />
                {/* unit picker — same searchable widget as the catalogue
                    picker. DispatchJobProduct.unit stores the unit NAME
                    (legacy column kept that way), so the picked id is
                    translated back to its `name` against the cached
                    units list. */}
                <LookupField
                  endpoint="/master-data/units"
                  labelKey="name"
                  value={units.find((u: any) => u.name === newUnit)?.id || null}
                  onChange={(id) => {
                    const name = id ? (units.find((u: any) => u.id === id)?.name ?? '') : '';
                    setNewUnit(name);
                  }}
                  placeholder="Unit"
                  className="h-11 text-sm lg:h-8 lg:text-xs"
                />
              </div>
              {/* Outline, not solid: stacked full-width on mobile this sits
                  directly above the footer's solid "Create", and two solid
                  primaries read as a pair of equal actions — so "Add" looked
                  like it belonged to the footer rather than to this subform.
                  Outline keeps it clearly subordinate and scoped to products. */}
              <Button
                variant="outline"
                className="h-11 lg:h-8"
                type="button"
                disabled={!newItem || !newQty}
                onClick={addStaged}
              >
                <Plus className="h-4 w-4 lg:h-3 lg:w-3" /> Add
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter className="px-4 py-3 border-t shrink-0 flex flex-col-reverse gap-2 lg:p-0 lg:border-0 lg:pt-2 lg:flex-row lg:gap-2 lg:justify-end">
          <Button variant="outline" className="h-11 lg:h-9 w-full lg:w-auto" onClick={onClose}>Cancel</Button>
          <Button
            className="h-11 lg:h-9 w-full lg:w-auto"
            onClick={() => create.mutate()}
            disabled={create.isPending || !assetId || !start || !end || !!newJobRecurrenceError || !!newJobTimeError || !!customerError}
            title={newJobTimeError ?? newJobRecurrenceError ?? customerError ?? undefined}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GenerateRunSheetDialog({
  defaultDate, onClose, onCreated,
}: { defaultDate: Date; onClose: () => void; onCreated: (newId?: string) => void }) {
  const navigate = useNavigate();
  // NZ YYYY-MM-DD. toISOString() would report the UTC day (a day behind through
  // an NZ morning); the runtime's local parts are wrong for offshore staff.
  const [date, setDate] = React.useState<string>(() => toDateInputValue(defaultDate));
  const [branchId, setBranchId] = React.useState<string>('');
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  // Merged Atlas + Contractor list — the run-sheet generator should select
  // from any vehicle the dispatcher would normally schedule. Uses the
  // dispatch-gated endpoint (same as the dashboard grid above) so a
  // dispatcher without Assets-report access can still drive the generator.
  const { data: assets = [] } = useQuery({
    queryKey: ['dispatch-grid-assets'],
    queryFn: async () => {
      const r = await api<AssetRow[]>('/fleet/dispatch-assets?limit=999');
      return r.data ?? [];
    },
  });

  const gen = useMutation({
    mutationFn: async () => {
      const r = await api('/run-sheets/generate', {
        method: 'POST',
        body: JSON.stringify({ date, branch: branchId || null, assetIds: [...selected] }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: (data: any) => {
      toast('Run sheet generated', `Covers ${data?.jobCount ?? 0} job(s)`, 'success');
      const newId = data?.runSheet?._id ?? data?.runSheet?.id;
      onCreated(newId);
      if (newId) navigate(`/dispatch-extras/run-sheets/${newId}/print`);
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Generate Run Sheet</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Day</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <Label>Branch (optional)</Label>
            <LookupField
              endpoint="/master-data/branches"
              labelKey="name"
              value={branchId || null}
              onChange={(id) => setBranchId(id ?? '')}
              placeholder="— Any —"
            />
          </div>
          <div>
            <Label>Fleet ({selected.size} selected)</Label>
            <div className="max-h-80 overflow-y-auto border rounded-md p-2 space-y-3">
              {(() => {
                // Bucket assets by VehicleGroup name so the dispatcher can
                // generate a run sheet group-by-group (Quarries Fleet, Wiri
                // Concrete, etc.). Anything without a group falls under
                // "Ungrouped" so it stays selectable.
                const buckets = new Map<string, typeof assets>();
                for (const a of assets) {
                  const key = a.vehicleGroup?.group?.trim() || 'Ungrouped';
                  if (!buckets.has(key)) buckets.set(key, []);
                  buckets.get(key)!.push(a);
                }
                const groupNames = [...buckets.keys()].sort((x, y) => {
                  if (x === 'Ungrouped') return 1;
                  if (y === 'Ungrouped') return -1;
                  return x.localeCompare(y);
                });
                return groupNames.map((name) => {
                  const rows = buckets.get(name)!;
                  const ids = rows.map((r) => r.id);
                  const allChecked = ids.every((id) => selected.has(id));
                  const someChecked = !allChecked && ids.some((id) => selected.has(id));
                  return (
                    <div key={name}>
                      <div className="flex items-center justify-between sticky top-0 bg-background z-10 py-1 px-1 border-b">
                        <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                          <input
                            type="checkbox"
                            checked={allChecked}
                            ref={(el) => { if (el) el.indeterminate = someChecked; }}
                            onChange={(e) => {
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) for (const id of ids) next.add(id);
                                else for (const id of ids) next.delete(id);
                                return next;
                              });
                            }}
                          />
                          {name}
                        </label>
                        <span className="text-xs text-muted-foreground">{rows.length}</span>
                      </div>
                      <div className="pl-5 mt-1 space-y-1">
                        {rows.map((a) => (
                          <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent/40 px-1 py-0.5 rounded">
                            <input
                              type="checkbox"
                              checked={selected.has(a.id)}
                              onChange={(e) => {
                                setSelected((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(a.id); else next.delete(a.id);
                                  return next;
                                });
                              }}
                            />
                            <span>{fillTemplate(CONFIG.assetRowTemplate, a)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            <div className="mt-1 flex gap-2 text-xs">
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set(assets.map((a) => a.id)))}>Select all</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => gen.mutate()}
            disabled={gen.isPending || selected.size === 0 || !date}
          >
            {gen.isPending ? 'Generating…' : 'Generate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Outcome of the last SaveJob for a line: sent (with time), failed (reason on hover), or never sent. */
function WeightraxLineBadge({ send }: { send: { sentAt?: string | null; error?: string | null; vehicleCode?: string | null } | null | undefined }) {
  if (!send) return <span className="text-muted-foreground">—</span>;
  if (send.error) {
    return <span className="text-destructive cursor-help" title={send.error}>Failed</span>;
  }
  const at = send.sentAt ? new Date(send.sentAt).toLocaleString('en-NZ', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  return <span className="text-green-700 dark:text-green-400" title={`Sent under ${send.vehicleCode ?? '?'}`}>Sent {at}</span>;
}

/** Weighed net weight (kg) for display; set by Weightrax, never edited here. */
function formatNetKg(v: number | null | undefined): string {
  return v == null ? '—' : v.toLocaleString();
}

/** Line item status — the same options as the dispatch job's own status. */
function LineStatusSelect({ value, onChange, className }: { value: string | null | undefined; onChange: (v: string) => void; className?: string }) {
  return (
    <Select value={value || SCHEDULED} onValueChange={onChange}>
      <SelectTrigger className={className} aria-label="Line status">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {DISPATCH_JOB_STATUSES.map((s) => (
          <SelectItem key={s} value={s}>
            <span className={cn('inline-block h-2 w-2 rounded-full mr-2 align-middle border', statusChipClass(s))} />{s}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Editable quantity; keeps its own text so a half-typed "1." isn't clobbered. */
function LineQtyInput({ value, onChange, className }: { value: number | null; onChange: (v: number | null) => void; className?: string }) {
  const [text, setText] = React.useState(value == null ? '' : String(value));
  React.useEffect(() => {
    if ((text === '' ? null : Number(text)) !== value) setText(value == null ? '' : String(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Input
      className={className} type="number" step="0.01" aria-label="Quantity" value={text}
      onChange={(e) => { setText(e.target.value); onChange(e.target.value === '' ? null : Number(e.target.value)); }}
    />
  );
}

/** Unit picker for a persisted line — stores the unit NAME, like the add-row. */
function LineUnitPicker({ units, value, onChange, className }: { units: any[]; value: string | null; onChange: (v: string | null) => void; className?: string }) {
  return (
    <LookupField
      endpoint="/master-data/units" labelKey="name" placeholder="Unit" className={className}
      value={units.find((u: any) => u.name === value)?.id || null}
      onChange={(id) => onChange(id ? (units.find((u: any) => u.id === id)?.name ?? null) : null)}
    />
  );
}

function EditDispatchJobDialog({
  jobId, onClose, onSaved, onDuplicate,
}: { jobId: string; onClose: () => void; onSaved: () => void; onDuplicate: (jobId: string) => void }) {
  const confirm = useConfirm();
  const { data, isLoading } = useQuery<any>({
    queryKey: ['/dispatch/jobs', jobId],
    // `?? null` so a 404 (deleted job) resolves to null instead of undefined
    // (which react-query rejects) — lets us show a proper not-found state below.
    queryFn: async () => (await api<any>(`/dispatch/jobs/${jobId}`)).data ?? null,
    // An edit surface must start from server truth, not a possibly-stale
    // cached copy (staleTime would otherwise skip the refetch for 30s).
    refetchOnMount: 'always',
  });
  const [form, setForm] = React.useState<any>({});
  // Seed/refresh the form from every data arrival UNTIL the user's first
  // actual edit, then freeze. Seeding once from a stale cached copy made the
  // dialog intermittently show old/empty fields for the whole open (fresh
  // refetch ignored); seeding on every arrival clobbered in-progress typing.
  // Dirty-tracking gives both: fields fill in as fresh data lands, and
  // nothing overwrites a form the user has started editing.
  const dirtyRef = React.useRef(false);
  const editForm = React.useCallback((updater: (p: any) => any) => {
    dirtyRef.current = true;
    setForm(updater);
  }, []);
  React.useEffect(() => {
    if (data && !dirtyRef.current) setForm(data);
  }, [data]);

  // Product line items (DispatchJobProduct subform).
  const { data: products = [] } = useQuery<any[]>({
    queryKey: ['/dispatch-job-products', jobId],
    queryFn: async () => (await api<any[]>(`/dispatch-job-products?dispatchJob=${jobId}&limit=200`)).data || [],
    enabled: !!jobId,
  });
  // Units list still cached because it's small (<200) and the unit
  // picker uses a plain Select. The catalogue list is no longer fetched
  // up-front — LookupField fetches its own server-side searched page.
  const { data: units = [] } = useQuery<any[]>({
    queryKey: ['/master-data/units'],
    queryFn: async () => (await api<any[]>('/master-data/units?limit=200')).data || [],
  });

  // Staged line-item edits. Every other field in this dialog persists on
  // Save only, and line items must match: until Save, the driver's job page
  // and the notification pipeline must not see them. `stagedAdds` carries
  // the picked CatalogueItem object so rows render code/description without
  // another lookup; `stagedRemoveIds` are persisted row ids pending delete.
  // Cancel/close simply discards both, like any other unsaved field edit.
  const [stagedAdds, setStagedAdds] = React.useState<Array<{ key: number; item: any; quantity: number; unit: string | null }>>([]);
  const [stagedRemoveIds, setStagedRemoveIds] = React.useState<Set<string>>(new Set());
  const stagedKeyRef = React.useRef(0);
  const visibleProducts = products.filter((p: any) => !stagedRemoveIds.has(p.id));
  // In-place edits to persisted rows, keyed by row id; only changed fields.
  // Same Save-only rule as adds/removes. A status change here is what rolls
  // the job up to Complete server-side once every weighbridge line is.
  type LineEdit = { quantity?: number | null; unit?: string | null; status?: string };
  const [stagedEdits, setStagedEdits] = React.useState<Record<string, LineEdit>>({});
  const lineValue = (p: any, key: keyof LineEdit) => (key in (stagedEdits[p.id] ?? {}) ? stagedEdits[p.id][key] : p[key]);
  const editLine = (p: any, patch: LineEdit) => setStagedEdits((prev) => {
    const next: LineEdit = { ...(prev[p.id] ?? {}), ...patch };
    // Drop fields put back to their saved value, so an untouched row sends nothing.
    for (const k of Object.keys(next) as Array<keyof LineEdit>) if (next[k] === p[k]) delete next[k];
    const out = { ...prev };
    if (Object.keys(next).length) out[p.id] = next; else delete out[p.id];
    return out;
  });

  const editCustomerError = customerPickerError({
    excludeFromWeightrax: !!form.excludeFromWeightrax,
    site: form.site ?? null,
    customerRef: refToId(form.customerRef), customer: form.customer ?? null, newCustomer: form.newCustomer ?? null,
  });
  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form };
      delete body.id;
      delete body._id;
      delete body.createdAt;
      delete body.updatedAt;
      // info is auto-derived server-side; status is changed via Mark Complete only.
      delete body.info;
      delete body.status;
      if (body.asset && typeof body.asset === 'object') body.asset = body.asset._id || body.asset.id;
      if (body.driver && typeof body.driver === 'object') body.driver = body.driver._id || body.driver.id;
      body.customerRef = refToId(body.customerRef);
      body.site = refToId(body.site);
      if (!body.newCustomer) delete body.newCustomer;
      const r = await api<any>(`/dispatch/jobs/${jobId}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Save failed');
      // The new customer now exists and is linked — swap the draft for the
      // link so a retry (e.g. after a product error below) can't create it twice.
      if (body.newCustomer) {
        setForm((p: any) => ({ ...p, newCustomer: undefined, customerRef: r.data?.customerRef ?? null, customer: r.data?.customer ?? p.customer }));
      }

      // Apply the staged line-item edits only now — this is the moment they
      // become visible to the driver (and trigger the job-updated
      // notification, deduped server-side). Each success is pruned from the
      // staged state immediately, so if one op fails the dialog stays open
      // and a retry only re-runs what's still pending.
      const productErrors: string[] = [];
      for (const id of Array.from(stagedRemoveIds)) {
        const d = await api(`/dispatch-job-products/${id}`, { method: 'DELETE' });
        // NOT_FOUND = the row was already deleted elsewhere — the desired end
        // state. Prune it like a success, or this Save could never complete.
        if (d.success || (d.error as any)?.code === 'NOT_FOUND') {
          setStagedRemoveIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
        } else {
          productErrors.push(d.error?.message || 'Failed to remove a product');
        }
      }
      for (const [id, patch] of Object.entries(stagedEdits)) {
        if (stagedRemoveIds.has(id)) continue;
        const u = await api(`/dispatch-job-products/${id}`, { method: 'PUT', body: JSON.stringify(patch) });
        if (u.success) setStagedEdits((prev) => { const n = { ...prev }; delete n[id]; return n; });
        else productErrors.push(u.error?.message || 'Failed to update a product');
      }
      for (const a of stagedAdds) {
        const c = await api('/dispatch-job-products', {
          method: 'POST',
          body: JSON.stringify({ dispatchJob: jobId, catalogueItem: a.item.id, quantity: a.quantity, unit: a.unit }),
        });
        if (c.success) setStagedAdds((prev) => prev.filter((x) => x.key !== a.key));
        else productErrors.push(c.error?.message || 'Failed to add a product');
      }
      if (productErrors.length) throw new Error(productErrors.join('; '));
    },
    onSuccess: () => {
      toast('Saved', undefined, 'success');
      onSaved();
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
    // Cache refresh (by-id, products, board, driver views, other tabs) is
    // handled centrally by lib/data-sync.ts off the write requests themselves.
  });

  // Manual (re)send of the weighbridge lines — normally automatic on Save.
  const sendToWeightrax = useMutation({
    mutationFn: async () => {
      const r = await api<any>(`/dispatch/jobs/${jobId}/weightrax-sync`, { method: 'POST' });
      if (!r.success) throw new Error(r.error?.message || 'Send failed');
      return r.data as { sent: number; failed: number };
    },
    onSuccess: (d) => {
      if (d.failed) toast('Weightrax', `${d.sent} sent, ${d.failed} failed — hover the line for the reason`, 'error');
      else toast('Sent to Weightrax', `${d.sent} line${d.sent === 1 ? '' : 's'}`, 'success');
    },
    onError: (e: any) => toast('Weightrax', e.message, 'error'),
  });

  const markComplete = useMutation({
    mutationFn: async () => {
      const r = await api(`/dispatch/jobs/${jobId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'Complete' }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => {
      toast('Marked complete', undefined, 'success');
      onSaved();
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  // Permission-gated delete. Server enforces `dispatch_jobs:delete` /
  // `delete_all` via the CRUD factory; this just exposes the affordance
  // on the dashboard so users don't have to pivot to the Dispatch Jobs
  // report just to remove a job.
  const { can } = usePermissions();
  const canDeleteJob = can('dispatch_jobs', 'delete');
  const canAddJob = can('dispatch_jobs', 'add');
  const deleteJob = useMutation({
    mutationFn: async () => {
      const r = await api(`/dispatch/jobs/${jobId}`, { method: 'DELETE' });
      if (!r.success) throw new Error(r.error?.message || 'Delete failed');
    },
    onSuccess: () => { toast('Job deleted', undefined, 'success'); onSaved(); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  // Local row for the new-product input. `newItem` holds the full picked
  // CatalogueItem (or null) so `addProduct` can submit the id directly.
  const [newItem, setNewItem] = React.useState<any | null>(null);
  const [newQty, setNewQty] = React.useState('');
  const [newUnit, setNewUnit] = React.useState('');

  // End must be after Start — canonical shared check, mirrored server-side
  // by the dispatchJobSchema refine.
  const timeRangeError = React.useMemo(
    () => validateTimeRange(form.start ?? null, form.end ?? null),
    [form.start, form.end],
  );

  // Block save when the recurring + duration combo would produce overlapping
  // spawns (e.g. a 7-day job set to recur daily). Reads from the live `form`
  // state so the error message updates as the user edits.
  const recurrenceError = React.useMemo(() => {
    if (!form.recurring) return null;
    return validateRecurringDuration(
      form.start ?? null,
      form.end ?? null,
      form.recurrencePattern?.cron ?? null,
      form.recurrencePattern?.timezone ?? null,
    );
  }, [form.recurring, form.start, form.end, form.recurrencePattern?.cron, form.recurrencePattern?.timezone]);

  if (isLoading) return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className={FULLSCREEN_DIALOG_CLASS}>
        <div className="p-6 text-sm text-muted-foreground">Loading…</div>
      </DialogContent>
    </Dialog>
  );
  if (!data) return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className={FULLSCREEN_DIALOG_CLASS}>
        <div className="p-6 space-y-3 text-sm text-muted-foreground">
          <p>This job no longer exists — it may have been deleted or reassigned.</p>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
  // NOTE: `form.driver` is passed to the picker as-is, populated object and
  // all. Flattening it to an id here is what made the field render a raw
  // ObjectId: LookupField seeds its label cache from a populated value, and
  // with only the id left it could only resolve names for drivers present in
  // the dropdown's fetched page — which is filtered to status Active. On this
  // database that is 11 of 104 employees, so 19,043 of 19,110 jobs with a
  // driver showed a hex string instead of a name.

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent hideClose className={FULLSCREEN_DIALOG_CLASS}>
        {/* Header — sticky/fixed at top on mobile/tablet so users always have context. */}
        {/* `sticky` below lg, not `shrink-0`: now that the whole dialog scrolls
            (see the body), the title would scroll away with it and the user would
            lose track of which job they're editing. Sticky keeps that context
            without pinning the footer too.
            `data-sticky-top` tells the keyboard-follow maths that this bar covers
            the top of the scrollport, so a focused field is never scrolled up
            behind it — without it, the field's own label disappears under here. */}
        <DialogHeader data-sticky-top className="max-lg:sticky max-lg:top-0 max-lg:z-10 max-lg:bg-background relative px-4 py-3 pr-12 border-b shrink-0 lg:p-0 lg:pr-12 lg:border-0 lg:pb-2">
          <DialogTitle className="flex items-center gap-2 text-base lg:text-lg">
            <span>Dispatch Job {form.jobNumber ?? ''}</span>
            {form.status && (
              <span className={`text-xs px-2 py-0.5 rounded-full border ${form.status === 'Complete' ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-800' : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800'}`}>
                {form.status}
              </span>
            )}
          </DialogTitle>
          {/* The X lives in the sticky bar, not in DialogContent (hence
              `hideClose`): the default one is positioned against the content
              box, so once the dialog scrolls as one piece it scrolls out of
              reach. Here it stays pinned with the title. */}
          <DialogClose className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        {/* Scrollable body — flex-1 fills the space between sticky header/footer
            on mobile/tablet; falls back to fixed-height scroll on desktop. */}
        {/* Below lg the body is NOT its own scroller: the whole dialog scrolls as
            one piece, so the footer flows after the content instead of being
            pinned above the keyboard. `flex-none` + `overflow-visible` hand the
            scrolling back to DialogContent. At lg+ the inner scroller returns, so
            the desktop modal keeps its fixed height with the actions in view. */}
        <div className="max-lg:flex-none max-lg:overflow-visible flex-1 overflow-y-auto px-4 py-4 space-y-4 text-sm lg:px-0 lg:py-0 lg:max-h-[70vh] lg:space-y-3">
          <div>
            <Label>Driver</Label>
            <DriverRefSelect value={form.driver} onChange={(v) => editForm((p: any) => ({ ...p, driver: v }))} />
          </div>
          <CustomerPicker
            value={{ excludeFromWeightrax: !!form.excludeFromWeightrax, site: form.site ?? null, customerRef: refToId(form.customerRef), customer: form.customer ?? null, newCustomer: form.newCustomer ?? null }}
            onChange={(v) => editForm((p: any) => ({ ...p, excludeFromWeightrax: v.excludeFromWeightrax, site: v.site, customerRef: v.customerRef, customer: v.customer, newCustomer: v.newCustomer ?? undefined }))}
          />
          {/* Stack date/time fields vertically on mobile/tablet so each gets a full row width. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-2">
            <div><Label>Start</Label><NzDateTimeInput ariaLabel="Start" withTime value={form.start ?? null} onChange={(d) => editForm((p: any) => ({ ...p, start: d ? d.toISOString() : null }))} /></div>
            <div><Label>End</Label><NzDateTimeInput ariaLabel="End" withTime value={form.end ?? null} onChange={(d) => editForm((p: any) => ({ ...p, end: d ? d.toISOString() : null }))} /></div>
          </div>
          {timeRangeError && (
            <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {timeRangeError}
            </div>
          )}

          {/* Job Number is server-allocated on create and immutable thereafter —
              read-only here to match the create dialog. */}
          <div><Label>Job Number</Label><Input className="h-11 lg:h-9 bg-muted/40" value={form.jobNumber ?? ''} readOnly title="Auto-allocated on creation; not editable." /></div>
          <div><Label>Job Details</Label><Input aria-label="Job Details" className="h-11 lg:h-9" value={form.jobDetails ?? ''} onChange={(e) => editForm((p: any) => ({ ...p, jobDetails: e.target.value }))} /></div>
          <div>
            <Label>Address</Label>
            {/* The whole subform, not just Line 1.

                This was a single autocomplete bound to `addressLine1`. The
                other parts were still stored and still saved — the spread
                preserved them — but nothing rendered them, so any address that
                did not arrive by picking a Mapbox suggestion showed one line
                and hid the rest. That is now the normal case: the partner API
                accepts a full structured address, so a job created through it
                displayed none of its city, region or postcode and gave an
                operator no way to see or correct one.

                `latitude`/`longitude` stay hidden deliberately — they are
                machine values from the geocoder or the API caller, not
                something to hand-type — and the spread keeps them intact. */}
            <div className="grid grid-cols-2 gap-2">
              {ADDRESS_FIELDS.map(([k, lbl]) => (
                <div key={k} className="col-span-2 md:col-span-1">
                  <Label className="text-xs text-muted-foreground">{lbl}</Label>
                  {k === 'addressLine1' ? (
                    <AddressAutocomplete
                      value={form.address?.[k] ?? ''}
                      onChange={(v) => editForm((p: any) => ({ ...p, address: { ...(p.address ?? {}), [k]: v } }))}
                      onSelect={(feat) => {
                        // Use the parsed `addressLine1` (house number +
                        // street), NOT `place_name`. place_name is the whole
                        // formatted string — reasonable when Line 1 was the
                        // only visible field, wrong now that city, postcode and
                        // country each have their own box, because it repeated
                        // them inside Line 1.
                        const parts = extractAddressParts(feat);
                        editForm((p: any) => ({ ...p, address: { ...(p.address ?? {}), ...parts } }));
                      }}
                      placeholder="Start typing an address…"
                      ariaLabel={`Job address — ${lbl}`}
                    />
                  ) : (
                    <Input
                      className="h-11 lg:h-9"
                      aria-label={`Job address — ${lbl}`}
                      value={form.address?.[k] ?? ''}
                      onChange={(e) => editForm((p: any) => ({
                        ...p, address: { ...(p.address ?? {}), [k]: e.target.value },
                      }))}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Recurring — toggling OFF nulls out recurrencePattern so the next
              tick won't try to re-spawn from this row. Editing the cron while
              ON only affects future spawns (already-spawned children are
              untouched, by design). Hidden behind RECURRING_UI_ENABLED —
              superseded by Duplicate. */}
          {RECURRING_UI_ENABLED && (
            <div className="border-t pt-4 lg:pt-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="recurring-edit-toggle" className="cursor-pointer">Recurring</Label>
                <Switch
                  id="recurring-edit-toggle"
                  checked={!!form.recurring}
                  onCheckedChange={(v) => editForm((p: any) => ({
                    ...p,
                    recurring: v,
                    recurrencePattern: v
                      ? (p.recurrencePattern ?? { cron: '0 6 * * 1-5', timezone: 'Pacific/Auckland' })
                      : null,
                  }))}
                />
              </div>
              {form.recurring && recurrenceError && (
                <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                  {recurrenceError}
                </div>
              )}
              {form.recurring && (
                <ScheduleBuilder
                  cron={form.recurrencePattern?.cron ?? '0 6 * * 1-5'}
                  onChange={(c) => editForm((p: any) => ({
                    ...p,
                    recurrencePattern: { cron: c, timezone: p.recurrencePattern?.timezone ?? 'Pacific/Auckland' },
                  }))}
                  allowCustom={false}
                />
              )}
            </div>
          )}

          {/* Product line items subform */}
          <div className="border-t pt-4 lg:pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label>Product line items ({visibleProducts.length + stagedAdds.length})</Label>
            </div>
            {visibleProducts.length === 0 && stagedAdds.length === 0 && (
              <div className="text-xs text-muted-foreground py-2">No products on this job.</div>
            )}
            {(visibleProducts.length > 0 || stagedAdds.length > 0) && (
              <>
                {/* Mobile/tablet: card list — touch-friendly rows. */}
                <div className="lg:hidden space-y-2">
                  {visibleProducts.map((p: any) => (
                    <div key={p.id} className="border rounded-md p-3 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{p.catalogueItem?.itemCode ?? '—'}</div>
                        {p.catalogueItem?.description && (
                          <div className="text-xs text-muted-foreground truncate">{p.catalogueItem.description}</div>
                        )}
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <LineQtyInput className="h-11 text-sm" value={lineValue(p, 'quantity') as number | null} onChange={(v) => editLine(p, { quantity: v })} />
                          <LineUnitPicker className="h-11 text-sm" units={units} value={lineValue(p, 'unit') as string | null} onChange={(v) => editLine(p, { unit: v })} />
                          <div className="col-span-2">
                            <LineStatusSelect className="h-11 text-sm" value={lineValue(p, 'status') as string} onChange={(v) => editLine(p, { status: v })} />
                          </div>
                          {p.netWeightKg != null && (
                            <div className="col-span-2 text-xs text-muted-foreground">Net weight (weighbridge): <span className="text-foreground">{formatNetKg(p.netWeightKg)} kg</span></div>
                          )}
                          {p.weightraxJob && (
                            <div className="col-span-2 text-xs text-muted-foreground">Weightrax: <WeightraxLineBadge send={p.weightraxJob} /></div>
                          )}
                        </div>
                        {stagedEdits[p.id] && <div className="text-[10px] text-amber-600 mt-1">(unsaved changes)</div>}
                      </div>
                      <button
                        type="button"
                        className="text-destructive text-lg leading-none px-2 py-1"
                        onClick={async () => { if (await confirm({ title: 'Remove this product?', description: 'It will be deleted when you save.', destructive: true, confirmLabel: 'Remove' })) setStagedRemoveIds((prev) => new Set(prev).add(p.id)); }}
                        aria-label="Remove product"
                      >×</button>
                    </div>
                  ))}
                  {stagedAdds.map((a) => (
                    <div key={a.key} className="border border-dashed rounded-md p-3 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {a.item?.itemCode ?? '—'} <span className="text-[10px] font-normal text-amber-600">(unsaved)</span>
                        </div>
                        {a.item?.description && (
                          <div className="text-xs text-muted-foreground truncate">{a.item.description}</div>
                        )}
                        <div className="text-xs text-muted-foreground mt-1">
                          Qty: <span className="text-foreground">{a.quantity}</span>
                          {a.unit ? <> · {a.unit}</> : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-destructive text-lg leading-none px-2 py-1"
                        onClick={() => setStagedAdds((prev) => prev.filter((x) => x.key !== a.key))}
                        aria-label="Remove product"
                      >×</button>
                    </div>
                  ))}
                </div>
                {/* Desktop: compact table. */}
                <table className="hidden lg:table w-full text-xs border rounded overflow-hidden">
                  <thead className="bg-muted/40 text-[11px]">
                    <tr>
                      <th className="text-left px-2 py-1">Item</th>
                      <th className="text-left px-2 py-1">Description</th>
                      <th className="text-right px-2 py-1 w-24">Qty</th>
                      <th className="text-left px-2 py-1 w-36">Unit</th>
                      <th className="text-left px-2 py-1 w-36">Status</th>
                      <th className="text-right px-2 py-1 w-24" title="Weighed net weight, reported by Weightrax when the line is completed">Net (kg)</th>
                      <th className="text-left px-2 py-1 w-24">Weightrax</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleProducts.map((p: any) => (
                      <tr key={p.id} className={cn('border-t', stagedEdits[p.id] && 'bg-amber-50/40 dark:bg-amber-950/10')}>
                        <td className="px-2 py-1">
                          {p.catalogueItem?.itemCode ?? '—'}
                          {stagedEdits[p.id] && <span className="text-[10px] text-amber-600"> (unsaved)</span>}
                        </td>
                        <td className="px-2 py-1 text-muted-foreground">{p.catalogueItem?.description ?? ''}</td>
                        <td className="px-1 py-1"><LineQtyInput className="h-8 text-xs text-right" value={lineValue(p, 'quantity') as number | null} onChange={(v) => editLine(p, { quantity: v })} /></td>
                        <td className="px-1 py-1"><LineUnitPicker className="h-8 text-xs" units={units} value={lineValue(p, 'unit') as string | null} onChange={(v) => editLine(p, { unit: v })} /></td>
                        <td className="px-1 py-1"><LineStatusSelect className="h-8 text-xs" value={lineValue(p, 'status') as string} onChange={(v) => editLine(p, { status: v })} /></td>
                        <td className="px-2 py-1 text-right text-muted-foreground">{formatNetKg(p.netWeightKg)}</td>
                        <td className="px-2 py-1"><WeightraxLineBadge send={p.weightraxJob} /></td>
                        <td className="px-2 py-1">
                          <button
                            className="text-destructive hover:underline text-xs"
                            onClick={async () => { if (await confirm({ title: 'Remove this product?', description: 'It will be deleted when you save.', destructive: true, confirmLabel: 'Remove' })) setStagedRemoveIds((prev) => new Set(prev).add(p.id)); }}
                          >×</button>
                        </td>
                      </tr>
                    ))}
                    {stagedAdds.map((a) => (
                      <tr key={a.key} className="border-t bg-amber-50/40 dark:bg-amber-950/10">
                        <td className="px-2 py-1">
                          {a.item?.itemCode ?? '—'} <span className="text-[10px] text-amber-600">(unsaved)</span>
                        </td>
                        <td className="px-2 py-1 text-muted-foreground">{a.item?.description ?? ''}</td>
                        <td className="px-2 py-1 text-right">{a.quantity}</td>
                        <td className="px-2 py-1">{a.unit ?? ''}</td>
                        <td className="px-2 py-1 text-muted-foreground">{SCHEDULED}</td>
                        <td className="px-2 py-1 text-right text-muted-foreground">—</td>
                        <td className="px-2 py-1 text-muted-foreground">—</td>
                        <td className="px-2 py-1">
                          <button
                            className="text-destructive hover:underline text-xs"
                            onClick={() => setStagedAdds((prev) => prev.filter((x) => x.key !== a.key))}
                          >×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Add-row — stack vertically on mobile/tablet, inline grid on desktop. */}
            <div className="mt-3 grid grid-cols-1 gap-2 lg:mt-2 lg:grid-cols-[1fr_80px_80px_auto] lg:gap-1">
              <LookupField
                endpoint="/catalogue-items"
                labelKey="itemCode"
                secondaryLabelKey="description"
                searchAllFields
                placeholder="Catalogue item…"
                className="h-11 text-sm lg:h-8 lg:text-xs"
                value={newItem?.id ?? null}
                onChange={(id) => { if (!id) setNewItem(null); }}
                onPick={(item) => {
                  setNewItem(item);
                  if (item?.unit) setNewUnit(item.unit);
                }}
              />
              <div className="grid grid-cols-2 gap-2 lg:contents">
                <Input className="h-11 text-sm lg:h-8 lg:text-xs" type="number" step="0.01" placeholder="Qty" value={newQty} onChange={(e) => setNewQty(e.target.value)} />
                {/* unit picker — same searchable widget as the catalogue
                    picker. DispatchJobProduct.unit stores the unit NAME
                    (legacy column kept that way), so the picked id is
                    translated back to its `name` against the cached
                    units list. */}
                <LookupField
                  endpoint="/master-data/units"
                  labelKey="name"
                  value={units.find((u: any) => u.name === newUnit)?.id || null}
                  onChange={(id) => {
                    const name = id ? (units.find((u: any) => u.id === id)?.name ?? '') : '';
                    setNewUnit(name);
                  }}
                  placeholder="Unit"
                  className="h-11 text-sm lg:h-8 lg:text-xs"
                />
              </div>
              <Button
                variant="outline"
                className="h-11 lg:h-8"
                disabled={!newItem || !newQty}
                onClick={() => {
                  if (!newItem) return;
                  stagedKeyRef.current += 1;
                  setStagedAdds((prev) => [...prev, { key: stagedKeyRef.current, item: newItem, quantity: Number(newQty), unit: newUnit || null }]);
                  setNewItem(null); setNewQty(''); setNewUnit('');
                }}
              ><Plus className="h-4 w-4 lg:h-3 lg:w-3" /> Add</Button>
            </div>
          </div>
        </div>
        {/* Footer — scrolls with the content on mobile/tablet, so it no longer
            competes with the keyboard for height and can go back to one
            full-width action per row; single row at lg+. */}
        <DialogFooter className="px-4 py-3 border-t shrink-0 flex flex-col-reverse gap-2 lg:p-0 lg:border-0 lg:pt-2 lg:flex-row lg:gap-2">
          {canDeleteJob && (
            <Button
              variant="destructive"
              className="h-11 lg:h-9 w-full lg:w-auto"
              onClick={async () => {
                if (await confirm({
                  title: `Delete dispatch job ${form.jobNumber ?? ''}?`,
                  description: 'This permanently removes the job and its product line items.',
                  destructive: true,
                  confirmLabel: 'Delete',
                })) deleteJob.mutate();
              }}
              disabled={deleteJob.isPending}
            >
              {deleteJob.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          )}
          {canAddJob && (
            <Button
              variant="secondary"
              className="h-11 lg:h-9 w-full lg:w-auto"
              onClick={() => onDuplicate(jobId)}
            >
              Duplicate
            </Button>
          )}
          {!form.excludeFromWeightrax && form.site && form.status !== 'Complete' && form.status !== 'Cancelled' && (
            <Button
              variant="secondary"
              className="h-11 lg:h-9 w-full lg:w-auto"
              title="Sends every open weighbridge line (product with a JDE code) to Weightrax. Happens automatically on Save."
              onClick={() => sendToWeightrax.mutate()}
              disabled={sendToWeightrax.isPending}
            >
              {sendToWeightrax.isPending ? 'Sending…' : 'Send to Weightrax'}
            </Button>
          )}
          {form.status !== 'Complete' && (
            <Button
              variant="secondary"
              className="h-11 lg:h-9 w-full lg:w-auto"
              onClick={async () => { if (await confirm({ title: 'Mark this dispatch job as complete?', confirmLabel: 'Mark Complete' })) markComplete.mutate(); }}
              disabled={markComplete.isPending}
            >
              {markComplete.isPending ? 'Marking…' : 'Mark Complete'}
            </Button>
          )}
          <div className="hidden lg:block lg:flex-1" />
          <Button variant="outline" className="h-11 lg:h-9 w-full lg:w-auto" onClick={onClose}>Cancel</Button>
          <Button
            className="h-11 lg:h-9 w-full lg:w-auto"
            onClick={() => save.mutate()}
            disabled={save.isPending || !!recurrenceError || !!timeRangeError || !!editCustomerError}
            title={timeRangeError ?? recurrenceError ?? undefined}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Duplicate an existing dispatch job into a brand-new record. Every field is
// carried verbatim from the source EXCEPT start/end (the user re-picks them),
// the job number (freshly allocated), and the status (reset to SCHEDULED).
// Product line items are copied too. Recurring is intentionally NOT carried —
// it's been retired from the UI (see RECURRING_UI_ENABLED).
function DuplicateDispatchJobDialog({
  jobId, onClose, onCreated,
}: { jobId: string; onClose: () => void; onCreated: () => void }) {
  // Source job (asset/driver come back populated) + its product subform rows.
  // refetchOnMount 'always': the POST body is built verbatim from `source`,
  // so duplicating from a stale cached copy would clone outdated fields
  // (wrong driver/customer) — always re-read the source before copying.
  const { data: source } = useQuery({
    queryKey: ['/dispatch/jobs', jobId],
    queryFn: async () => (await api<any>(`/dispatch/jobs/${jobId}`)).data,
    refetchOnMount: 'always',
  });
  const { data: products = [] } = useQuery<any[]>({
    queryKey: ['/dispatch-job-products', jobId],
    queryFn: async () => (await api<any[]>(`/dispatch-job-products?dispatchJob=${jobId}&limit=200`)).data || [],
    refetchOnMount: 'always',
  });

  const [start, setStart] = React.useState<Date | null>(null);
  const [end, setEnd] = React.useState<Date | null>(null);
  const [jobNumber, setJobNumber] = React.useState('');
  // End must be after Start — same shared check as the other dialogs / server.
  const dupTimeError = React.useMemo(() => validateTimeRange(start, end), [start, end]);

  // Prefill the pickers from the source times once it loads — the dispatcher
  // usually shifts them, but starting at the original avoids an empty form.
  // Once only: a background refetch of the source job (data-sync) must not
  // reset times the dispatcher already re-picked.
  const prefilledRef = React.useRef(false);
  React.useEffect(() => {
    if (!source || prefilledRef.current) return;
    prefilledRef.current = true;
    setStart(source.start ? new Date(source.start) : null);
    setEnd(source.end ? new Date(source.end) : null);
  }, [source]);

  // Allocate-on-open, same contract as the create dialog: the new record gets
  // its own number so it never collides with the source's.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await api<{ jobNumber: string }>('/dispatch/jobs/allocate-number', { method: 'POST' });
      if (!cancelled && r.success && r.data?.jobNumber) setJobNumber(r.data.jobNumber);
    })();
    return () => { cancelled = true; };
  }, []);

  const refId = (v: any) => (v && typeof v === 'object' ? (v._id || v.id) : v) || null;

  const create = useMutation({
    mutationFn: async () => {
      if (!source) throw new Error('Source job not loaded yet');
      const body = {
        asset: refId(source.asset),
        driver: refId(source.driver),
        customer: source.customer ?? null,
        customerRef: refId(source.customerRef),
        site: refId(source.site),
        excludeFromWeightrax: !!source.excludeFromWeightrax,
        phoneNumber: source.phoneNumber ?? null,
        jobDetails: source.jobDetails ?? null,
        address: source.address ?? null,
        jobNumber: jobNumber.trim() || null,
        status: SCHEDULED,
        start: start ? start.toISOString() : null,
        end: end ? end.toISOString() : null,
      };
      const r = await api<any>('/dispatch/jobs', { method: 'POST', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Duplicate failed');
      const newId = r.data?.id || r.data?._id;
      // Copy product line items onto the new job (catalogueItem comes back
      // populated; unit is a plain string).
      if (newId && products.length > 0) {
        for (const p of products) {
          const pr = await api('/dispatch-job-products', {
            method: 'POST',
            body: JSON.stringify({
              dispatchJob: newId,
              catalogueItem: refId(p.catalogueItem),
              quantity: p.quantity,
              unit: p.unit ?? null,
            }),
          });
          if (!pr.success) throw new Error(pr.error?.message || 'Failed to copy product');
        }
      }
    },
    onSuccess: () => { toast('Dispatch job duplicated', undefined, 'success'); onCreated(); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const assetLabel = source?.asset
    ? [source.asset.fleetNumber, source.asset.registrationNumber].filter(Boolean).join(' - ')
    : '—';

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="lg:max-w-md">
        <DialogHeader>
          <DialogTitle>Duplicate Dispatch Job</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {/* Read-only confirmation of what's being carried over. */}
          <div className="rounded-md border bg-muted/40 px-3 py-2 space-y-1 text-xs">
            <div><span className="text-muted-foreground">Asset:</span> {assetLabel}</div>
            {source?.customer && <div><span className="text-muted-foreground">Customer:</span> {source.customer}</div>}
            <div><span className="text-muted-foreground">New job #:</span> {jobNumber || '…'}</div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-2">
            <div><Label>Start</Label><NzDateTimeInput ariaLabel="Start" withTime value={start} onChange={setStart} /></div>
            <div><Label>End</Label><NzDateTimeInput ariaLabel="End" withTime value={end} onChange={setEnd} /></div>
          </div>
          {dupTimeError && (
            <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {dupTimeError}
            </div>
          )}
        </div>
        <DialogFooter className="flex flex-col-reverse gap-2 lg:flex-row lg:gap-2 lg:justify-end">
          <Button variant="outline" className="h-11 lg:h-9 w-full lg:w-auto" onClick={onClose}>Cancel</Button>
          <Button
            className="h-11 lg:h-9 w-full lg:w-auto"
            onClick={() => create.mutate()}
            disabled={create.isPending || !source || !start || !end || !!dupTimeError}
            title={dupTimeError ?? undefined}
          >
            {create.isPending ? 'Duplicating…' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DriverRefSelect({ value, onChange }: { value: unknown; onChange: (v: string | null) => void }) {
  // Mirror the schedule drag-create dialog: active employees only, A→Z,
  // with the same debounced server-side search as the other ref pickers.
  return (
    <LookupField
      endpoint="/employees"
      labelKey="fullName"
      filter={EMPLOYEE_REF_QUERY}
      // Populated object or bare id — LookupField's refToId handles both, and
      // an object additionally seeds the label cache so an archived driver
      // still shows their name rather than an id.
      value={(value as any) || null}
      onChange={(id) => onChange(id)}
      placeholder="— Select —"
    />
  );
}

